/**
 * AdminBackordersBoard
 * --------------------
 * Unified command-center view for ALL backorder requests (manual + homepage +
 * hospital portal). Replaces the legacy pipeline view with a denser, more
 * actionable layout:
 *
 *   ┌──────────────────────────────────────────────────────────────────┐
 *   │ KPI strip (clickable filters)                       [+ New BO]   │
 *   ├──────────────────────────────────────────────────────────────────┤
 *   │ Status tabs · Search · Refresh                                   │
 *   ├──────────────────────────────────────────────────────────────────┤
 *   │ Request list (table)                                             │
 *   │   Row click → slide-out detail drawer with action toolbar        │
 *   └──────────────────────────────────────────────────────────────────┘
 */

import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/App";
import { resolveBackorderPhotoUrl, extractImageUrlsFromText } from "@/utils/backorderPhoto";
import {
  Search, Plus, RefreshCw, Mail, Send, Edit3, X, ChevronRight, Eye,
  AlertCircle, CheckCircle2, Clock, Package, Truck, MapPin, Hospital,
  Loader2, FileText, MessageSquare, ListChecks, Pause, Trash2, Users,
  Archive, Globe, ExternalLink, Maximize2, Minimize2, Wrench,
  Image as ImageIcon, FileScan, Droplets, Banknote, Receipt,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import SourceFromSuppliersModal from "@/components/admin/SourceFromSuppliersModal";
import DIPoDispatchDialog from "@/components/admin/DIPoDispatchDialog";
import ManualQuoteEntryDialog from "@/components/admin/ManualQuoteEntryDialog";
import CrmContactPicker from "@/components/admin/CrmContactPicker";
import BackorderCustomerQuoteTab from "@/components/admin/BackorderCustomerQuoteTab";
import BackorderCustomerOrderTab from "@/components/admin/BackorderCustomerOrderTab";
import NewOrderFromPoModal from "@/components/admin/NewOrderFromPoModal";
import BackorderFulfillmentTab from "@/components/admin/BackorderFulfillmentTab";
import SupplierPayablesPanel from "@/components/admin/SupplierPayablesPanel";
import BackorderAwardPOPanel from "@/components/admin/BackorderAwardPOPanel";
import BackorderRecipientsPanel from "@/components/admin/BackorderRecipientsPanel";
import SupplierDiscoveryPanel from "@/components/admin/SupplierDiscoveryPanel";
import BackorderInvoiceClosePanel from "@/components/admin/BackorderInvoiceClosePanel";
import InvoiceAuditDesk from "@/components/admin/InvoiceAuditDesk";
import TrainingLauncher from "@/components/training/TrainingLauncher";

const API = process.env.REACT_APP_BACKEND_URL;

// Status → pill style
const STATUS_META = {
  new:               { label: "New",              cls: "bg-blue-50 text-blue-700 border-blue-200" },
  sourcing:          { label: "Sourcing",         cls: "bg-amber-50 text-amber-700 border-amber-200" },
  sent_to_suppliers: { label: "RFQ Sent",         cls: "bg-amber-50 text-amber-700 border-amber-200" },
  quotes_received:   { label: "Quotes In",        cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  awarded:           { label: "Awarded",          cls: "bg-violet-50 text-violet-700 border-violet-200" },
  partially_shipped: { label: "Partially Shipped", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  shipped:           { label: "Shipped",           cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  fulfilled:         { label: "Fulfilled",        cls: "bg-slate-100 text-slate-700 border-slate-300" },
  on_hold:           { label: "On Hold",          cls: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  cancelled:         { label: "Cancelled",        cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const SOURCE_META = {
  quick_quote:    { label: "Web Form",  cls: "bg-blue-50 text-blue-700" },
  manual:         { label: "Manual",    cls: "bg-amber-50 text-amber-700" },
  hospital_portal:{ label: "Hospital",  cls: "bg-violet-50 text-violet-700" },
  po_intake:      { label: "PO Scan",   cls: "bg-cyan-50 text-cyan-700" },
  quickbooks:     { label: "QuickBooks", cls: "bg-emerald-50 text-emerald-700" },
  di_tank:        { label: "DI Tank",   cls: "bg-sky-50 text-sky-700" },
};

function fmtAge(iso) {
  if (!iso) return "—";
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || { label: status || "—", cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border ${meta.cls}`}>{meta.label}</span>;
}

// Every hospital PO scanned/saved against a request, newest capture style first.
// Build Order stores either customer_order.pos[] (multi-PO) or a single po_number.
function hospitalPos(bo) {
  const co = bo?.customer_order || {};
  const out = [];
  for (const p of co.pos || []) {
    const v = String(p?.po_number || "").trim();
    if (v && !out.includes(v)) out.push(v);
  }
  // NB: top-level `po_number` is OUR supplier PO (mirrored to payables) — never
  // the hospital's, so it is deliberately excluded here.
  for (const v of [co.po_number, bo?.customer_po_number, bo?.customer_po_parse?.po_number]) {
    const s = String(v || "").trim();
    if (s && !out.includes(s)) out.push(s);
  }
  return out;
}


function SourceBadge({ source }) {
  const meta = SOURCE_META[source] || { label: source || "—", cls: "bg-slate-100 text-slate-500" };
  return <span className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${meta.cls}`}>{meta.label}</span>;
}

// ============================================================================
export default function AdminBackordersBoard() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showNewOrder, setShowNewOrder] = useState(false);
  const [showPoIntake, setShowPoIntake] = useState(false);
  // Filter by order_type (backorder | maintenance_repair | all)
  const [orderTypeFilter, setOrderTypeFilter] = useState("all");
  // Deep-link: /admin/backorders?open=<id>&tab=build — used by the DI Tank
  // Exchange board to hand an exchange straight to Build Quote.
  const [deepLinkTab, setDeepLinkTab] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const openId = params.get("open");
    if (!openId) return;
    setSelectedId(openId);
    setDeepLinkTab(params.get("tab") || null);
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/backorders`, {
        headers,
        // "historical" is a frontend pseudo-filter — backend doesn't know
        // about it, so pass status="all" and we'll filter client-side below.
        params: { status: statusFilter === "historical" ? "all" : statusFilter, q: search || undefined, limit: 200 },
      });
      const all = r.data.items || [];
      setItems(statusFilter === "historical" ? all.filter((it) => it.is_historical) : all);
    } catch (e) {
      toast.error("Failed to load backorders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter]);

  // Delete a backorder (admin endpoint, wipes related quotes too).
  // Called from the row trash icon AND the drawer toolbar. Closes the
  // drawer if the deleted item happens to be open.
  const deleteBackorder = async (id, { skipConfirm } = {}) => {
    if (!skipConfirm) {
      const yes = window.confirm(
        `Delete backorder ${id}?\n\nThis also removes any supplier quotes attached to it. This cannot be undone.`,
      );
      if (!yes) return false;
    }
    try {
      const r = await axios.delete(`${API}/api/admin/backorders/${id}`, { headers });
      const wiped = r.data?.deleted_quotes ?? 0;
      toast.success(`Deleted ${id}${wiped ? ` (+ ${wiped} quote${wiped === 1 ? "" : "s"})` : ""}`);
      if (selectedId === id) setSelectedId(null);
      // Optimistic remove from state, then refresh in background
      setItems((prev) => prev.filter((it) => it.id !== id));
      load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
      return false;
    }
  };

  // Live KPI counts (computed from full unfiltered set when statusFilter is "all";
  // when filtered we still show the same counts using current items)
  const stats = useMemo(() => {
    const base = { all: items.length, new: 0, sourcing: 0, quotes_received: 0, awarded: 0, fulfilled: 0, on_hold: 0, cancelled: 0, mr: 0, wop: 0 };
    items.forEach((it) => {
      const s = it.status || "new";
      const key = (s === "sent_to_suppliers") ? "sourcing" : s;
      if (base[key] !== undefined) base[key] += 1;
      if (it.order_type === "maintenance_repair") base.mr += 1;
      if (it.order_type === "work_order_parts") base.wop += 1;
    });
    return base;
  }, [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      // Order type filter
      if (orderTypeFilter === "backorder"
        && ["maintenance_repair", "work_order_parts"].includes(it.order_type)) return false;
      if (orderTypeFilter === "maintenance_repair" && it.order_type !== "maintenance_repair") return false;
      if (orderTypeFilter === "work_order_parts" && it.order_type !== "work_order_parts") return false;
      if (!q) return true;
      const hay = `${it.id || ""} ${it.hospital_name || ""} ${it.item_name || ""} ${it.contact_name || ""} ${it.contact_email || ""} ${hospitalPos(it).join(" ")}`.toLowerCase();
      return hay.includes(q);
    });
  }, [items, search, orderTypeFilter]);

  const selected = useMemo(() => items.find((i) => i.id === selectedId) || null, [items, selectedId]);

  // A deep-linked request that fell outside the current filter/page still has
  // to open — pull that one record in and merge it into the list.
  useEffect(() => {
    if (!selectedId || selected || loading) return;
    let alive = true;
    axios.get(`${API}/api/admin/backorders`, { headers, params: { status: "all", q: selectedId, limit: 5 } })
      .then((r) => {
        const hit = (r.data?.items || []).find((it) => it.id === selectedId);
        if (alive && hit) setItems((prev) => (prev.some((p) => p.id === hit.id) ? prev : [hit, ...prev]));
      })
      .catch(() => {});
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, selected, loading]);

  return (
    <div className="space-y-5" data-testid="admin-backorders-board">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Sourcing Desk</h1>
          <p className="text-sm text-slate-500">
            All inbound RFQs &mdash; medical supply backorders <span className="text-slate-400">&middot;</span> maintenance &amp; repair work orders. Manual entries, homepage Quick-Quote, and hospital portal &mdash; in one place.
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Looking for live customer orders / sales? They live in the <a href="/admin/orders" className="text-blue-600 hover:underline font-medium">Orders Window</a>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <TrainingLauncher trackId="sourcing" />
          <Button onClick={() => setShowNew(true)} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="bo-new-btn">
            <Plus className="w-4 h-4" /> New Request
          </Button>
          <Button onClick={() => setShowNewOrder(true)} className="bg-emerald-600 hover:bg-emerald-700 gap-1.5" data-testid="bo-new-order-btn" title="Skip the RFQ — enter a known order with its customer PO">
            <Plus className="w-4 h-4" /> New Order
          </Button>
          <Button onClick={() => setShowPoIntake(true)} className="bg-violet-600 hover:bg-violet-700 gap-1.5"
            data-testid="new-order-from-po-btn"
            title="Upload the customer's PO — AskIsaac reads it and builds the whole order">
            <FileScan className="w-4 h-4" /> New Order from PO
          </Button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {[
          { key: "all",             label: "All",          icon: Package,     color: "bg-slate-100 text-slate-700" },
          { key: "new",             label: "New",          icon: AlertCircle, color: "bg-blue-50 text-blue-700" },
          { key: "sourcing",        label: "Sourcing",     icon: Send,        color: "bg-amber-50 text-amber-700" },
          { key: "quotes_received", label: "Quotes In",    icon: ListChecks,  color: "bg-emerald-50 text-emerald-700" },
          { key: "awarded",         label: "Awarded",      icon: CheckCircle2,color: "bg-violet-50 text-violet-700" },
          { key: "fulfilled",       label: "Completed",    icon: CheckCircle2,color: "bg-emerald-50 text-emerald-700" },
          { key: "historical",      label: "Historical",   icon: Archive,     color: "bg-slate-100 text-slate-700" },
          { key: "on_hold",         label: "On Hold",      icon: Pause,       color: "bg-yellow-50 text-yellow-700" },
        ].map((k) => {
          const active = statusFilter === k.key;
          return (
            <button
              key={k.key}
              onClick={() => setStatusFilter(k.key)}
              className={`text-left rounded-xl border p-3 transition-all ${active ? "border-amber-400 ring-2 ring-amber-100" : "border-slate-200 hover:border-slate-300 hover:shadow-sm"} bg-white`}
              data-testid={`bo-kpi-${k.key}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-7 h-7 rounded-md flex items-center justify-center ${k.color}`}>
                  <k.icon className="w-3.5 h-3.5" />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{k.label}</span>
              </div>
              <div className="text-2xl font-extrabold text-slate-900 leading-none mt-1">{stats[k.key] ?? 0}</div>
            </button>
          );
        })}
      </div>

      {/* Payables moved to its own desk — /admin/payables — so this board stays
          about sourcing. The scoped invoice panel still sits inside each order. */}
      <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 flex flex-wrap items-center gap-3"
        data-testid="payables-desk-link">
        <div className="min-w-0">
          <p className="text-[12.5px] font-black text-slate-900">Payables moved to its own desk</p>
          <p className="text-[11px] text-slate-500">
            Invoice Audit, payments &amp; runs and PO reconciliation now live under
            Sourcing Desk → Payables, so this board stays about sourcing.
          </p>
        </div>
        <a href="/admin/payables?view=audit"
          className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-sky-700 px-3 py-1.5 text-[11.5px] font-bold text-white hover:bg-sky-800"
          data-testid="open-payables-desk">
          Open the payables desk
        </a>
      </div>

      {/* Order-type filter (Backorder vs Maintenance & Repair). Stays in
          sync with the KPI strip — clicking a pill narrows the table to
          that order type without touching the status filter. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="bo-order-type-filter">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Type:</span>
        {[
          { key: "all", label: `All (${items.length})`, cls: "bg-slate-100 text-slate-700 border-slate-200" },
          { key: "backorder", label: `Backorder (${items.length - stats.mr - stats.wop})`, cls: "bg-amber-50 text-amber-800 border-amber-300" },
          { key: "maintenance_repair", label: `Maintenance & Repair (${stats.mr})`, cls: "bg-indigo-50 text-indigo-800 border-indigo-300" },
          { key: "work_order_parts", label: `WO Parts (${stats.wop})`, cls: "bg-rose-50 text-rose-800 border-rose-300" },
        ].map((opt) => (
          <button
            key={opt.key}
            type="button"
            onClick={() => setOrderTypeFilter(opt.key)}
            className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-full border transition-colors ${
              orderTypeFilter === opt.key
                ? `${opt.cls} ring-2 ring-offset-1 ring-slate-300`
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
            }`}
            data-testid={`bo-order-type-pill-${opt.key}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Search by PO #, ID, hospital, item, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
            data-testid="bo-search"
          />
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1.5">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          Refresh
        </Button>
      </div>

      {/* Request List */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="px-4 py-2 text-left">Request</th>
                <th className="px-4 py-2 text-left">Items</th>
                <th className="px-4 py-2 text-left">Hospital</th>
                <th className="px-4 py-2 text-left">Ship-to</th>
                <th className="px-4 py-2 text-left">Freight</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Sourcing</th>
                <th className="px-4 py-2 text-left">Age</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Loading…</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-500">No backorder requests match.</td></tr>
              )}
              {!loading && filtered.map((it) => {
                const items = it.items || [];
                const firstItem = items[0]?.name || it.item_name || "—";
                const extra = items.length > 1 ? `+${items.length - 1} more` : "";
                return (
                  <tr key={it.id} onClick={() => setSelectedId(it.id)} className="hover:bg-amber-50/40 cursor-pointer transition-colors" data-testid={`bo-row-${it.id}`}>
                    <td className="px-4 py-2.5 align-top">
                      <div className="flex flex-col gap-1">
                        <span className="font-mono text-xs font-bold text-slate-900">{it.id}</span>
                        {(() => {
                          const pos = hospitalPos(it);
                          if (pos.length === 0) return null;
                          return (
                            <span
                              className="inline-flex items-center gap-1 self-start whitespace-nowrap font-mono text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-800 border border-indigo-200"
                              title={`Hospital PO${pos.length > 1 ? "s" : ""}: ${pos.join(", ")}`}
                              data-testid={`bo-row-hospital-po-${it.id}`}
                            >
                              <Receipt className="w-2.5 h-2.5" />
                              PO {pos[0]}
                              {pos.length > 1 && (
                                <span className="font-sans font-bold">+{pos.length - 1}</span>
                              )}
                            </span>
                          );
                        })()}
                        <div className="flex items-center gap-1 flex-wrap">
                          <SourceBadge source={it.source} />
                          {it.order_type === "maintenance_repair" && (
                            <span
                              className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200"
                              title="Maintenance & Repair work order"
                              data-testid={`bo-row-mr-badge-${it.id}`}
                            >
                              <Wrench className="w-2.5 h-2.5" /> M&amp;R
                            </span>
                          )}
                          {it.order_type === "work_order_parts" && (
                            <span
                              className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200"
                              title={`Backordered parts for work order ${it.work_order_link?.work_order_id || ""}`}
                              data-testid={`bo-row-wo-parts-badge-${it.id}`}
                            >
                              <Wrench className="w-2.5 h-2.5" /> WO Parts
                            </span>
                          )}
                          {(() => {
                            // "Has photo" badge — surfaces the request as visually
                            // rich so admins can triage image-attached tickets first.
                            // True when any line item has photo_url OR notes text
                            // contains an image URL (QuickQuote / homepage flow).
                            const hasItemPhoto = (it.items || []).some((li) => li && li.photo_url);
                            const hasNotesPhoto = extractImageUrlsFromText(it.notes || "").length > 0;
                            if (!hasItemPhoto && !hasNotesPhoto) return null;
                            return (
                              <span
                                className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
                                title="Customer attached one or more photos"
                                data-testid={`bo-row-photo-badge-${it.id}`}
                              >
                                <ImageIcon className="w-2.5 h-2.5" /> Photo
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <div className="text-sm font-medium text-slate-900 line-clamp-1">{firstItem}</div>
                      {extra && <div className="text-[11px] text-slate-500">{extra}</div>}
                    </td>
                    <td className="px-4 py-2.5 align-top text-sm text-slate-700">
                      <div className="flex items-center gap-1 truncate">
                        <Hospital className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                        <span className="truncate">{it.hospital_name || "—"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 align-top text-sm text-slate-700">
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5 text-slate-400" />
                        {it.shipping_address || "—"}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      {it.use_hospital_courier ? (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">Hospital courier</span>
                      ) : (
                        <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">Supplier-paid</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <StatusPill status={it.status} />
                      {it.is_historical && (
                        <span
                          className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-600 bg-slate-100 border border-slate-300 rounded px-1.5 py-0.5"
                          title="Backfilled record — no emails were sent."
                          data-testid={`bo-historical-badge-${it.id}`}
                        >
                          <Archive className="w-2.5 h-2.5" /> Historical
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 align-top">
                      <div className="text-xs text-slate-700">
                        <span className="font-bold">{it.recipients_count || 0}</span> emailed · <span className="font-bold text-emerald-700">{it.quotes_count || 0}</span> quoted
                      </div>
                      {it.recipients_count > 0 && (
                        <div className="h-1 rounded bg-slate-100 mt-1 overflow-hidden">
                          <div
                            className="h-1 bg-emerald-500"
                            style={{ width: `${Math.min(100, Math.round(((it.quotes_count || 0) / Math.max(1, it.recipients_count)) * 100))}%` }}
                          />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 align-top text-xs text-slate-500">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {fmtAge(it.created_at)}
                      </div>
                      {it.closed_at && (
                        <div className="mt-1 text-[10px] text-emerald-700 font-semibold inline-flex items-center gap-1" data-testid={`bo-closed-${it.id}`}>
                          ✓ Closed {fmtAge(it.closed_at)}
                          {it.invoice_number && <span className="text-slate-500 font-mono"> · {it.invoice_number}</span>}
                          {it.invoice_total_billed != null && <span className="text-slate-600"> · ${Number(it.invoice_total_billed).toFixed(2)}</span>}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 align-top text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          onClick={(e) => { e.stopPropagation(); setSelectedId(it.id); }}
                          className="text-amber-700 hover:text-amber-800 inline-flex items-center gap-1 text-xs font-semibold px-1.5 py-1 rounded hover:bg-amber-50"
                          data-testid={`bo-row-open-${it.id}`}
                        >
                          Open <ChevronRight className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteBackorder(it.id); }}
                          className="text-slate-400 hover:text-rose-600 p-1 rounded hover:bg-rose-50 transition-colors"
                          title="Delete request"
                          data-testid={`bo-row-delete-${it.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <BackorderDetailDrawer
          backorder={selected}
          token={token}
          initialTab={deepLinkTab}
          onClose={() => { setSelectedId(null); setDeepLinkTab(null); }}
          onRefresh={load}
          onDelete={() => deleteBackorder(selected.id)}
          onOpenOrder={(id) => setSelectedId(id)}
        />
      )}

      {/* New Backorder modal — reuses existing CreateBackorderModal */}
      {showNew && (
        <NewBackorderInline open={showNew} onClose={() => setShowNew(false)} onSuccess={load} token={token} />
      )}

      {/* Customer PO → AskIsaac builds the order */}
      <NewOrderFromPoModal
        open={showPoIntake}
        onOpenChange={setShowPoIntake}
        token={token}
        onCreated={(newId) => { load(); if (newId) setSelectedId(newId); }}
      />

      {/* New Order modal — same form in direct-order mode (RFQ bypassed) */}
      {showNewOrder && (
        <NewBackorderInline
          open={showNewOrder}
          directOrder
          onClose={() => setShowNewOrder(false)}
          onSuccess={(newId) => { load(); if (newId) setSelectedId(newId); }}
          token={token}
        />
      )}
    </div>
  );
}

// ============================================================================
// Detail drawer
// ============================================================================
function BackorderDetailDrawer({ backorder, token, onClose, onRefresh, onDelete, onOpenOrder, initialTab }) {
  const [tab, setTab] = useState(initialTab || "details");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [quotes, setQuotes] = useState([]);
  const [showSourcing, setShowSourcing] = useState(false);
  const [showDiPo, setShowDiPo] = useState(false);
  const [showManualQuote, setShowManualQuote] = useState(false);
  // Editing-existing-quote dialog: holds the quote object whose Edit button
  // was clicked. Set to null when not editing.
  const [editingQuote, setEditingQuote] = useState(null);
  const [emailMenuOpen, setEmailMenuOpen] = useState(false);
  const [notifyMenuOpen, setNotifyMenuOpen] = useState(false);
  const [quoteView, setQuoteView] = useState("cards");
  // Default = half-page drawer (max-w-2xl). Expanded = full-page so admin
  // sees Build Quote + supplier quote cards side-by-side comfortably.
  const [drawerExpanded, setDrawerExpanded] = useState(false);
  // When the admin clicks "Accept" on a supplier quote card, we stash that
  // quote here and switch the tab to "build" — BackorderCustomerQuoteTab
  // uses it to pre-fill line items (supplier_cost from the accepted quote;
  // unit_price auto-derived via the current markup %).
  const [acceptedQuote, setAcceptedQuote] = useState(null);
  const [deployingDi, setDeployingDi] = useState(false);

  // Sold DI tank exchange → tracked deployment (84-day exchange clock).
  const deployDiTank = async () => {
    setDeployingDi(true);
    try {
      const r = await axios.post(
        `${API}/api/di-tanks/from-sourcing-request/${backorder.id}`, {},
        { headers: { Authorization: `Bearer ${token}` } });
      const d = r.data?.deployment;
      toast.success(`Deployment ${d?.id} created — exchange due ${d?.exchange_date}`);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create the deployment");
    } finally {
      setDeployingDi(false);
    }
  };

  const headers = { Authorization: `Bearer ${token}` };

  // Sync draft when a new backorder is selected
  useEffect(() => {
    setDraft({
      items: backorder.items || [],
      shipping_address: backorder.shipping_address || "",
      use_hospital_courier: !!backorder.use_hospital_courier,
      hospital_courier_account: backorder.hospital_courier_account || "",
      hospital_name: backorder.hospital_name || "",
      notes: backorder.notes || "",
      admin_notes: backorder.admin_notes || "",
      status: backorder.status || "new",
      // Default to ON (true) when the field is missing — only explicit `false`
      // mutes hospital comms. Maintains backward-compat with old records.
      notify_hospital: backorder.notify_hospital !== false,
    });
    setEditing(false);
  }, [backorder.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load quotes for this backorder. Tries the new admin-specific list endpoint
  // first (covers admin-manual quotes), then falls back to older variants.
  const reloadQuotes = () => {
    return axios.get(`${API}/api/admin/backorders/${backorder.id}/supplier-quotes`, { headers })
      .then((r) => setQuotes(r.data?.quotes || []))
      .catch(() => {
        return axios.get(`${API}/api/quotes/${backorder.id}`, { headers })
          .then((r) => setQuotes(r.data?.quotes || r.data || []))
          .catch(() => {
            return axios.get(`${API}/api/admin/quotes?backorder_id=${backorder.id}`, { headers })
              .then((r) => setQuotes(r.data?.quotes || []))
              .catch(() => setQuotes([]));
          });
      });
  };
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/admin/backorders/${backorder.id}/supplier-quotes`, { headers })
      .then((r) => { if (alive) setQuotes(r.data?.quotes || []); })
      .catch(() => {
        axios.get(`${API}/api/quotes/${backorder.id}`, { headers })
          .then((r) => { if (alive) setQuotes(r.data?.quotes || r.data || []); })
          .catch(() => {
            axios.get(`${API}/api/admin/quotes?backorder_id=${backorder.id}`, { headers })
              .then((r) => { if (alive) setQuotes(r.data?.quotes || []); })
              .catch(() => alive && setQuotes([]));
          });
      });
    return () => { alive = false; };
  }, [backorder.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveEdits = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/api/admin/backorders/${backorder.id}`, draft, { headers });
      toast.success("Backorder updated");
      setEditing(false);
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (status) => {
    try {
      await axios.patch(`${API}/api/admin/backorders/${backorder.id}`, { status }, { headers });
      toast.success(`Marked ${STATUS_META[status]?.label || status}`);
      onRefresh?.();
    } catch (e) {
      toast.error("Failed to update status");
    }
  };

  const toggleNotifyHospital = async () => {
    const next = !(draft.notify_hospital !== false);
    setDraft((d) => ({ ...d, notify_hospital: next }));
    try {
      await axios.patch(`${API}/api/admin/backorders/${backorder.id}`, { notify_hospital: next }, { headers });
      toast.success(next ? "Hospital comms enabled" : "Hospital comms muted");
      onRefresh?.();
    } catch (_e) {
      toast.error("Failed to update toggle");
      setDraft((d) => ({ ...d, notify_hospital: !next }));
    }
  };

  const archiveBackorder = async () => {
    if (!window.confirm("Archive this backorder? It will be hidden from the active board (history preserved).")) return;
    try {
      await axios.patch(`${API}/api/admin/backorders/${backorder.id}`, { archived: true }, { headers });
      toast.success("Backorder archived");
      onClose?.();
      onRefresh?.();
    } catch (_e) {
      toast.error("Failed to archive");
    }
  };


  // Hard-delete a supplier quote (admin only). Used for duplicates / test rows
  // that shouldn't roll into the "X quotes received" counter.
  const deleteQuote = async (quote) => {
    const supplierName = quote.supplier_name || "this supplier";
    if (!window.confirm(`Delete the quote from ${supplierName}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/admin/supplier-quotes/${quote.id}`, { headers });
      toast.success(`Quote from ${supplierName} deleted`);
      reloadQuotes();
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const emailHospital = async (template) => {
    setEmailMenuOpen(false);
    // Quotes Ready: prompt for the markup % to apply to supplier costs before
    // the hospital sees the pricing. Default: 30 % (matches our Customer Quote
    // tab default). Admin can override on each send.
    let markupPercent = 30;
    if (template === "quotes_ready") {
      const raw = window.prompt(
        "Markup % to apply on top of the supplier cost for this quote (e.g. 30 for +30%):",
        String(markupPercent),
      );
      if (raw === null) return; // admin cancelled
      const parsed = parseFloat(raw);
      if (Number.isNaN(parsed) || parsed < 0) {
        toast.error("Invalid markup — must be a positive number.");
        return;
      }
      markupPercent = parsed;
    }
    try {
      const url =
        `${API}/api/admin/backorders/${backorder.id}/email-hospital-draft` +
        `?template=${template}` +
        (template === "quotes_ready" ? `&markup_percent=${markupPercent}` : "");
      const r = await axios.get(url, { headers });
      window.location.href = r.data.mailto;
      const mm = r.data.markup_percent;
      toast.success(
        mm != null
          ? `Outlook draft opened — to ${r.data.to} · ${mm}% markup applied`
          : `Outlook draft opened — to ${r.data.to}`,
      );
    } catch (e) {
      toast.error("Could not build email draft");
    }
  };

  // Email the transaction record / receipt to the ordering party. Available
  // on historical records that capture unit cost, customer PO, date shipped,
  // and freight. Surfaces a small prompt for the recipient + CC list.
  const emailTransactionRecord = async (template = "transaction_record") => {
    const templateLabels = {
      transaction_record: "Transaction Record (Receipt)",
      order_confirmation: "Order Confirmation",
      shipping_notification: "Shipping Notification + Tracking",
      delivery_confirmation: "Delivery Confirmation",
      delay_notification: "Delay Notification",
    };
    const label = templateLabels[template] || template;
    setNotifyMenuOpen(false);
    const defaultTo = backorder.contact_email || "";
    const to = window.prompt(
      `Send "${label}" to (email):`,
      defaultTo
    );
    if (!to || !to.includes("@")) {
      if (to !== null) toast.error("A valid email is required");
      return;
    }
    const ccRaw = window.prompt(
      "CC additional recipients (comma-separated, optional):",
      ""
    );
    const cc_emails = (ccRaw || "")
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.includes("@"));
    try {
      const r = await axios.post(
        `${API}/api/admin/backorders/${backorder.id}/email-record`,
        { to_email: to.trim(), cc_emails, template },
        { headers }
      );
      toast.success(
        `${label} sent to ${r.data.to}${r.data.cc?.length ? ` (cc ${r.data.cc.length})` : ""}`
      );
      onRefresh?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Could not send ${label}`);
    }
  };

  // Download the current backorder rendered as a PDF for printing/filing.
  // Honors the same `template` switch so each notification can be downloaded
  // as a PDF version of the same email body the customer would receive.
  const downloadTransactionPdf = async (template = "transaction_record") => {
    setNotifyMenuOpen(false);
    try {
      const r = await axios.get(
        `${API}/api/admin/backorders/${backorder.id}/transaction-record/pdf?template=${template}`,
        { headers, responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      const po = backorder.customer_po_number || "no-po";
      a.download = `Sterile_Mate_${template}_${backorder.id}_PO_${po}.pdf`.replace(/\s+/g, "_");
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error("Could not generate PDF");
    }
  };

  const setItem = (idx, field, val) => {
    setDraft((d) => ({
      ...d,
      items: d.items.map((it, i) => (i === idx ? { ...it, [field]: val } : it)),
    }));
  };

  const addItem = () => {
    setDraft((d) => ({
      ...d,
      items: [
        ...(d.items || []),
        { item_type: "part", name: "", sku: "", description: "", quantity: 1, uom: "Each", notes: "", substitute: null },
      ],
    }));
    // Ensure edit mode is on so the new row is actually usable
    setEditing(true);
  };

  const removeItem = (idx) => {
    const it = (draft.items || [])[idx] || {};
    const label = it.name || it.sku || `line ${idx + 1}`;
    if (!window.confirm(`Remove "${label}" from this backorder? Save changes to make it permanent.`)) return;
    setDraft((d) => ({
      ...d,
      items: (d.items || []).filter((_, i) => i !== idx),
    }));
  };

  return (
    <div className="fixed inset-0 z-40 bg-black/40" onClick={onClose} data-testid="bo-detail-drawer">
      <div
        className={`absolute right-0 top-0 bottom-0 bg-white shadow-2xl overflow-hidden flex flex-col transition-all duration-200 ${
          drawerExpanded ? "w-full max-w-[96vw]" : "w-full max-w-2xl"
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="font-mono text-xs font-bold text-slate-900">{backorder.id}</span>
              <SourceBadge source={backorder.source} />
              {backorder.order_type === "maintenance_repair" && (
                <span
                  className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200"
                  title="Maintenance & Repair work order"
                  data-testid="bo-drawer-mr-badge"
                >
                  <Wrench className="w-2.5 h-2.5" /> M&amp;R
                </span>
              )}
              {backorder.order_type === "work_order_parts" && (
                <span
                  className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-rose-50 text-rose-700 border border-rose-200"
                  title="Backordered parts opened from a work order"
                  data-testid="bo-drawer-wo-parts-badge"
                >
                  <Wrench className="w-2.5 h-2.5" /> WO Parts
                </span>
              )}
              <StatusPill status={backorder.status} />
              {backorder.is_historical && (
                <span
                  className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-600 bg-slate-100 border border-slate-300 rounded px-1.5 py-0.5 ml-1"
                  title="Backfilled record — no emails were sent."
                  data-testid={`bo-drawer-historical-badge`}
                >
                  <Archive className="w-2.5 h-2.5" /> Historical
                </span>
              )}
            </div>
            <h2 className="text-lg font-bold text-slate-900">{backorder.hospital_name || "—"}</h2>
            <p className="text-xs text-slate-500">{fmtAge(backorder.created_at)}</p>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => setDrawerExpanded((v) => !v)}
              className="text-slate-400 hover:text-slate-700 p-1.5 rounded hover:bg-slate-100 transition-colors"
              title={drawerExpanded ? "Collapse to half-page" : "Expand to full page"}
              data-testid="bo-drawer-expand-toggle"
            >
              {drawerExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1.5 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
          </div>
        </div>

        {/* Action toolbar */}
        <div className="px-5 py-3 border-b border-slate-200 flex flex-wrap items-center gap-2 bg-white">
          <Button size="sm" onClick={() => setShowSourcing(true)} className="bg-amber-600 hover:bg-amber-700 gap-1.5" data-testid="bo-drawer-source">
            <Send className="w-3.5 h-3.5" /> Source from Suppliers & Contractors
          </Button>
          <Button size="sm" variant="outline" onClick={() => setShowDiPo(true)} className="border-cyan-300 text-cyan-700 hover:bg-cyan-50 gap-1.5" data-testid="bo-drawer-di-po">
            <FileText className="w-3.5 h-3.5" /> Send Service PO
          </Button>
          <div className="relative">
            <Button size="sm" variant="outline" onClick={() => setEmailMenuOpen((v) => !v)} className="gap-1.5" data-testid="bo-drawer-email">
              <Mail className="w-3.5 h-3.5" /> Email Hospital ▾
            </Button>
            {emailMenuOpen && (
              <div className="absolute top-full left-0 mt-1 z-50 bg-white border border-slate-200 rounded-md shadow-lg w-56 py-1" onMouseLeave={() => setEmailMenuOpen(false)}>
                <button onClick={() => emailHospital("status_update")} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50">📊 Status Update</button>
                <button onClick={() => emailHospital("quotes_ready")} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50">💵 Quotes Ready</button>
                <button onClick={() => emailHospital("custom")} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50">✍️ Custom Draft</button>
              </div>
            )}
          </div>
          <Button size="sm" variant="outline" onClick={() => setEditing((v) => !v)} className="gap-1.5">
            <Edit3 className="w-3.5 h-3.5" /> {editing ? "Cancel edit" : "Edit"}
          </Button>
          {backorder.is_historical && (
            <div className="relative">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setNotifyMenuOpen((v) => !v)}
                className="gap-1.5 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50 hover:border-emerald-300"
                title="Send a customer notification (receipt, order confirmation, shipping, delivery, delay)"
                data-testid="bo-drawer-notify-menu"
              >
                <Send className="w-3.5 h-3.5" /> Send Notification <ChevronRight className="w-3 h-3 rotate-90" />
                </Button>
                {notifyMenuOpen && (
                  <div
                    className="absolute z-50 mt-1 left-0 w-72 bg-white border border-slate-200 rounded-md shadow-lg py-1"
                    onMouseLeave={() => setNotifyMenuOpen(false)}
                    data-testid="bo-drawer-notify-menu-list"
                  >
                    <button
                      onClick={() => emailTransactionRecord("quote_sent")}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-start gap-2"
                      data-testid="bo-notify-quote"
                    >
                      <span className="text-base leading-none mt-0.5">💬</span>
                      <span>
                        <span className="block font-semibold text-slate-800">Send Quote</span>
                        <span className="block text-[11px] text-slate-500">Itemized quote &mdash; valid 30 days</span>
                      </span>
                    </button>
                    <button
                      onClick={() => emailTransactionRecord("order_confirmation")}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-start gap-2"
                      data-testid="bo-notify-order-confirmation"
                    >
                      <span className="text-base leading-none mt-0.5">✅</span>
                      <span>
                        <span className="block font-semibold text-slate-800">Order Confirmation</span>
                        <span className="block text-[11px] text-slate-500">&ldquo;We got your PO, here&apos;s what&apos;s next&rdquo;</span>
                      </span>
                    </button>
                    <button
                      onClick={() => emailTransactionRecord("shipping_notification")}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-start gap-2"
                      data-testid="bo-notify-shipping"
                    >
                      <span className="text-base leading-none mt-0.5">🚚</span>
                      <span>
                        <span className="block font-semibold text-slate-800">Shipping Notification</span>
                        <span className="block text-[11px] text-slate-500">&ldquo;It shipped &mdash; here&apos;s your tracking&rdquo;</span>
                      </span>
                    </button>
                    <button
                      onClick={() => emailTransactionRecord("partial_shipment")}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-start gap-2"
                      data-testid="bo-notify-partial"
                    >
                      <span className="text-base leading-none mt-0.5">📦</span>
                      <span>
                        <span className="block font-semibold text-slate-800">Partial Shipment</span>
                        <span className="block text-[11px] text-slate-500">Some lines shipped, balance to follow</span>
                      </span>
                    </button>
                    <button
                      onClick={() => emailTransactionRecord("delivery_confirmation")}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-start gap-2"
                      data-testid="bo-notify-delivery"
                    >
                      <span className="text-base leading-none mt-0.5">📬</span>
                      <span>
                        <span className="block font-semibold text-slate-800">Delivery Confirmation</span>
                        <span className="block text-[11px] text-slate-500">&ldquo;Delivered &mdash; please retain for records&rdquo;</span>
                      </span>
                    </button>
                    <button
                      onClick={() => emailTransactionRecord("delay_notification")}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-start gap-2"
                      data-testid="bo-notify-delay"
                    >
                      <span className="text-base leading-none mt-0.5">⏱️</span>
                      <span>
                        <span className="block font-semibold text-slate-800">Delay Notification</span>
                        <span className="block text-[11px] text-slate-500">&ldquo;Heads up &mdash; shipment is delayed&rdquo;</span>
                      </span>
                    </button>
                    <div className="border-t border-slate-200 my-1"></div>
                    <button
                      onClick={() => emailTransactionRecord("transaction_record")}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-start gap-2"
                      data-testid="bo-notify-transaction-record"
                    >
                      <span className="text-base leading-none mt-0.5">🧾</span>
                      <span>
                        <span className="block font-semibold text-slate-800">Final Transaction Record</span>
                        <span className="block text-[11px] text-slate-500">AP-ready receipt with totals</span>
                      </span>
                    </button>
                    <button
                      onClick={() => downloadTransactionPdf("transaction_record")}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-start gap-2"
                      data-testid="bo-notify-download-pdf"
                    >
                      <span className="text-base leading-none mt-0.5">⬇️</span>
                      <span>
                        <span className="block font-semibold text-slate-800">Download as PDF</span>
                        <span className="block text-[11px] text-slate-500">Save / print the transaction record</span>
                      </span>
                    </button>
                  </div>
                )}
            </div>
          )}
          {(backorder.di_tank_source || backorder.order_type === "di_tank_exchange") && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => deployDiTank()}
              disabled={deployingDi || !!backorder.di_tank_deployment_id}
              className="gap-1.5 text-sky-700 hover:text-sky-800 hover:bg-sky-50 hover:border-sky-300"
              title={backorder.di_tank_deployment_id
                ? `Already deployed as ${backorder.di_tank_deployment_id}`
                : "Create the tracked DI tank deployment (84-day exchange clock) from this order"}
              data-testid="bo-drawer-di-deploy"
            >
              <Droplets className="w-3.5 h-3.5" />
              {backorder.di_tank_deployment_id ? `Deployed · ${backorder.di_tank_deployment_id}` : "Deploy DI tank"}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={onDelete}
            className="gap-1.5 text-rose-600 hover:text-rose-700 hover:bg-rose-50 hover:border-rose-300"
            data-testid="bo-drawer-delete"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => archiveBackorder()}
            className="gap-1.5 text-amber-700 hover:text-amber-800 hover:bg-amber-50 hover:border-amber-300"
            data-testid="bo-drawer-archive"
          >
            <Archive className="w-3.5 h-3.5" /> Archive
          </Button>
          {/* Notify Hospital toggle — when OFF, the system suppresses all
              automated hospital comms (emails + portal notifications) for
              this backorder during early sourcing. */}
          <button
            type="button"
            onClick={() => toggleNotifyHospital()}
            className={`ml-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-bold transition-colors ${
              draft.notify_hospital !== false
                ? "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                : "bg-slate-100 border-slate-300 text-slate-500 hover:bg-slate-200"
            }`}
            title={draft.notify_hospital !== false
              ? "Hospital is receiving automated updates. Click to mute (draft mode)."
              : "Hospital comms muted. Click to enable updates."}
            data-testid="bo-notify-hospital-toggle"
            data-state={draft.notify_hospital !== false ? "on" : "off"}
          >
            <span className={`w-2 h-2 rounded-full ${draft.notify_hospital !== false ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
            Notify Hospital · {draft.notify_hospital !== false ? "ON" : "OFF"}
          </button>
          <select
            value={draft.status || ""}
            onChange={(e) => changeStatus(e.target.value)}
            className="ml-auto text-xs border border-slate-300 rounded-md px-2 py-1.5"
            data-testid="bo-drawer-status-select"
          >
            {Object.entries(STATUS_META).map(([k, m]) => (
              <option key={k} value={k}>{m.label}</option>
            ))}
          </select>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-3 border-b border-slate-200 flex gap-3 overflow-x-auto scrollbar-hide">
          {[
            { id: "details",  label: "Details",      icon: FileText },
            { id: "quotes",   label: `Quotes (${quotes.length})`, icon: ListChecks },
            { id: "build",    label: "Build Quote",  icon: Mail },
            { id: "order",    label: "Build Order",  icon: Package },
            { id: "fulfillment", label: "Fulfillment", icon: Truck },
            { id: "payables", label: "Supplier Payables", icon: Banknote },
            { id: "trade",    label: "Trade Intel",  icon: Globe },
            { id: "log",      label: "Sourcing Log", icon: MessageSquare },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`pb-2 border-b-2 text-xs font-bold uppercase tracking-wider inline-flex items-center gap-1.5 whitespace-nowrap shrink-0 transition-colors ${
                tab === t.id ? "border-amber-600 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {tab === "details" && (
            <div className="space-y-4">
              {backorder.order_type === "work_order_parts" && backorder.work_order_link && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 flex items-start justify-between gap-3"
                  data-testid="bo-wo-parts-callout">
                  <div>
                    <p className="text-xs font-bold text-rose-900 uppercase tracking-wider">
                      Backordered parts for a work order
                    </p>
                    <p className="text-xs text-rose-800 mt-0.5">
                      Work order <strong>{backorder.work_order_link.work_order_id}</strong>
                      {backorder.work_order_link.site ? ` · ${backorder.work_order_link.site}` : ""}
                      {backorder.work_order_link.contractor_name
                        ? ` · crew: ${backorder.work_order_link.contractor_name}`
                        : ""}
                      . The job is flagged <strong>parts on backorder</strong> until this request is
                      delivered.
                    </p>
                  </div>
                  <a
                    href={`/admin/work-orders?open=${backorder.work_order_link.work_order_id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="shrink-0 text-xs font-bold text-rose-800 underline hover:text-rose-900"
                    data-testid="bo-wo-parts-open-wo"
                  >
                    Open work order →
                  </a>
                </div>
              )}
              <BackorderAwardPOPanel
                backorder={backorder}
                token={token}
                onRefresh={onRefresh}
              />
              <BackorderRecipientsPanel
                backorder={backorder}
                token={token}
                onRefresh={onRefresh}
              />
              <SupplierDiscoveryPanel
                backorder={backorder}
                token={token}
                onRefresh={onRefresh}
              />
              <BackorderInvoiceClosePanel
                backorder={backorder}
                token={token}
                onRefresh={onRefresh}
              />
              <InvoiceAuditDesk
                token={token}
                linkType="backorder"
                linkId={backorder.id}
                compact
              />
              {/* Items */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
                    <Package className="w-3.5 h-3.5" /> Items
                  </h3>
                  <button
                    type="button"
                    onClick={addItem}
                    className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 hover:text-amber-900 hover:bg-amber-50 border border-amber-300 rounded px-2 py-1"
                    data-testid="bo-drawer-add-item"
                    title="Add a new line item to this backorder"
                  >
                    <Plus className="w-3 h-3" /> Add item
                  </button>
                </div>
                <div className="space-y-2">
                  {(draft.items || []).length === 0 && <p className="text-sm text-slate-500">No items.</p>}
                  {(draft.items || []).map((it, idx) => (
                    <div key={idx} className="bg-slate-50 border border-slate-200 rounded p-2 space-y-1.5">
                      {/* Admin edit/add provenance badges */}
                      {(it.admin_added || it.admin_edited_at) && (
                        <div className="flex flex-wrap items-center gap-1.5">
                          {it.admin_added && (
                            <span
                              className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-300"
                              title={`Added by ${it.admin_added_by || "admin"}${it.admin_added_at ? ` on ${new Date(it.admin_added_at).toLocaleString()}` : ""}`}
                              data-testid={`bo-item-added-badge-${idx}`}
                            >
                              <Plus className="w-2.5 h-2.5" /> Added by admin
                            </span>
                          )}
                          {it.admin_edited_at && !it.admin_added && (
                            <span
                              className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-800 border border-indigo-300"
                              title={`Edited by ${it.admin_edited_by || "admin"}${it.admin_edited_at ? ` on ${new Date(it.admin_edited_at).toLocaleString()}` : ""}${(it.admin_edit_fields || []).length ? ` — changed: ${(it.admin_edit_fields || []).join(", ")}` : ""}`}
                              data-testid={`bo-item-edited-badge-${idx}`}
                            >
                              <Edit3 className="w-2.5 h-2.5" /> Edited by admin
                              {(it.admin_edit_fields || []).length > 0 && (
                                <span className="font-normal normal-case text-indigo-600">
                                  · {(it.admin_edit_fields || []).join(", ")}
                                </span>
                              )}
                            </span>
                          )}
                        </div>
                      )}
                      {/* Customer-uploaded photo (if any) */}
                      {it.photo_url && (
                        <div className="flex items-start gap-2 pb-1.5 border-b border-slate-200/60">
                          <a
                            href={resolveBackorderPhotoUrl(it.photo_url)}
                            target="_blank"
                            rel="noreferrer"
                            title="Open full-size photo"
                            data-testid={`bo-item-photo-${idx}`}
                            className="flex-shrink-0"
                          >
                            <img
                              src={resolveBackorderPhotoUrl(it.photo_url)}
                              alt="Customer-uploaded reference"
                              className="w-16 h-16 rounded object-cover cursor-zoom-in hover:ring-2 hover:ring-slate-400 bg-white"
                              onError={(e) => { e.currentTarget.dataset.errored = "1"; e.currentTarget.style.display = "none"; e.currentTarget.nextElementSibling && (e.currentTarget.nextElementSibling.style.display = "flex"); }}
                            />
                            <div style={{ display: "none" }} className="w-16 h-16 rounded bg-red-50 border border-red-200 items-center justify-center text-[9px] text-red-600 font-bold text-center px-1">Photo unavailable</div>
                          </a>
                          <div className="flex-1 min-w-0 pt-0.5">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Customer photo</div>
                            <a href={resolveBackorderPhotoUrl(it.photo_url)} target="_blank" rel="noreferrer" className="text-[11px] text-slate-600 hover:text-slate-900 hover:underline break-all" data-testid={`bo-item-photo-link-${idx}`}>
                              {it.photo_url.split("/").pop()}
                            </a>
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <input
                          disabled={!editing}
                          value={it.name || ""}
                          onChange={(e) => setItem(idx, "name", e.target.value)}
                          className="col-span-5 bg-white border border-slate-300 rounded px-2 py-1 text-sm disabled:bg-transparent disabled:border-transparent"
                          placeholder="Item name"
                          data-testid={`bo-item-name-${idx}`}
                        />
                        <input
                          disabled={!editing}
                          value={it.sku || ""}
                          onChange={(e) => setItem(idx, "sku", e.target.value)}
                          className="col-span-3 bg-white border border-slate-300 rounded px-2 py-1 text-xs font-mono disabled:bg-transparent disabled:border-transparent"
                          placeholder="SKU / MFR #"
                          data-testid={`bo-item-sku-${idx}`}
                        />
                        <input
                          disabled={!editing}
                          type="number"
                          min="0"
                          value={it.quantity || 0}
                          onChange={(e) => setItem(idx, "quantity", parseInt(e.target.value) || 0)}
                          className="col-span-1 bg-white border border-slate-300 rounded px-2 py-1 text-sm text-right disabled:bg-transparent disabled:border-transparent"
                          placeholder="Qty"
                          data-testid={`bo-item-qty-${idx}`}
                        />
                        <select
                          disabled={!editing}
                          value={it.uom || "Each"}
                          onChange={(e) => setItem(idx, "uom", e.target.value)}
                          className="col-span-2 bg-white border border-slate-300 rounded px-1 py-1 text-xs disabled:bg-transparent disabled:border-transparent disabled:appearance-none"
                          data-testid={`bo-item-uom-${idx}`}
                        >
                          {["Each", "Pair", "Box", "Case", "Pack", "Roll", "Bottle", "Kit", "Set", "Bag", "Bundle", "Foot", "Meter", "Gallon", "Liter", "Pound", "Kilogram"].map((u) => (
                            <option key={u} value={u}>{u}</option>
                          ))}
                        </select>
                        {editing && (
                          <button
                            type="button"
                            onClick={() => removeItem(idx)}
                            className="col-span-1 flex items-center justify-center text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded p-1"
                            title="Remove this item"
                            data-testid={`bo-drawer-remove-item-${idx}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                      {/* Description — always visible when there is content, editable in edit mode */}
                      {(editing || (it.description || "").trim()) && (
                        <textarea
                          disabled={!editing}
                          value={it.description || ""}
                          onChange={(e) => setItem(idx, "description", e.target.value)}
                          placeholder="Item description (goes into the RFQ email to suppliers)"
                          rows={2}
                          className="w-full bg-white border border-slate-300 rounded px-2 py-1 text-xs disabled:bg-transparent disabled:border-transparent resize-none"
                          data-testid={`bo-item-description-${idx}`}
                        />
                      )}
                      {/* Notes — internal per-line note */}
                      {(editing || (it.notes || "").trim()) && (
                        <input
                          disabled={!editing}
                          value={it.notes || ""}
                          onChange={(e) => setItem(idx, "notes", e.target.value)}
                          placeholder="Notes (e.g. urgency, brand preference)"
                          className="w-full bg-white border border-slate-300 rounded px-2 py-1 text-xs disabled:bg-transparent disabled:border-transparent italic"
                          data-testid={`bo-item-notes-${idx}`}
                        />
                      )}
                      {/* Substitute sub-row */}
                      {(it.substitute || editing) && (
                        <div className="ml-0">
                          {it.substitute ? (
                            <div className="grid grid-cols-12 gap-2 items-center bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                              <span className="col-span-12 sm:col-span-1 text-[10px] font-bold uppercase tracking-wider text-amber-700">Sub →</span>
                              <input
                                disabled={!editing}
                                value={it.substitute.name || ""}
                                onChange={(e) => setItem(idx, "substitute", { ...(it.substitute || {}), name: e.target.value })}
                                className="col-span-12 sm:col-span-5 bg-white border border-amber-300 rounded px-2 py-1 text-xs disabled:bg-transparent disabled:border-transparent"
                                placeholder="Substitute name"
                              />
                              <input
                                disabled={!editing}
                                value={it.substitute.sku || ""}
                                onChange={(e) => setItem(idx, "substitute", { ...(it.substitute || {}), sku: e.target.value })}
                                className="col-span-8 sm:col-span-3 bg-white border border-amber-300 rounded px-2 py-1 text-xs font-mono disabled:bg-transparent disabled:border-transparent"
                                placeholder="Substitute SKU"
                              />
                              <input
                                disabled={!editing}
                                value={it.substitute.notes || ""}
                                onChange={(e) => setItem(idx, "substitute", { ...(it.substitute || {}), notes: e.target.value })}
                                className="col-span-4 sm:col-span-2 bg-white border border-amber-300 rounded px-2 py-1 text-xs disabled:bg-transparent disabled:border-transparent"
                                placeholder="Why"
                              />
                              {editing && (
                                <button
                                  type="button"
                                  onClick={() => setItem(idx, "substitute", null)}
                                  className="col-span-12 sm:col-span-1 text-rose-500 hover:text-rose-700 text-xs"
                                >Remove</button>
                              )}
                            </div>
                          ) : (
                            editing && (
                              <button
                                type="button"
                                onClick={() => setItem(idx, "substitute", { name: "", sku: "", notes: "" })}
                                className="text-[11px] font-semibold text-amber-700 hover:text-amber-800"
                              >+ Add substitute option</button>
                            )
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Ship-to & freight */}
              <section className="grid sm:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" /> Ship-to
                  </h3>
                  <input
                    disabled={!editing}
                    value={draft.shipping_address || ""}
                    onChange={(e) => setDraft((d) => ({ ...d, shipping_address: e.target.value }))}
                    placeholder="City, ST ZIP"
                    className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-sm disabled:bg-slate-50"
                  />
                </div>
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1.5">
                    <Truck className="w-3.5 h-3.5" /> Freight
                  </h3>
                  <label className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      disabled={!editing}
                      checked={!!draft.use_hospital_courier}
                      onChange={(e) => setDraft((d) => ({ ...d, use_hospital_courier: e.target.checked }))}
                      className="accent-amber-600"
                    />
                    Use hospital 3rd-party courier
                  </label>
                  {draft.use_hospital_courier && (
                    <input
                      disabled={!editing}
                      value={draft.hospital_courier_account || ""}
                      onChange={(e) => setDraft((d) => ({ ...d, hospital_courier_account: e.target.value }))}
                      placeholder="Account #"
                      className="w-full mt-2 bg-white border border-slate-300 rounded px-3 py-1.5 text-sm disabled:bg-slate-50"
                    />
                  )}
                </div>
              </section>

              {/* Hospital name (internal) */}
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Hospital (internal reference)</h3>
                <input
                  disabled={!editing}
                  value={draft.hospital_name || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, hospital_name: e.target.value }))}
                  className="w-full bg-white border border-slate-300 rounded px-3 py-2 text-sm disabled:bg-slate-50"
                />
              </section>

              {/* Notes from requestor */}
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Notes (from requestor)</h3>
                {/* Auto-detect image URLs pasted in notes (e.g. QuickQuote Homepage-Form uploads
                    prefixed as "[Photo attached] https://…jpg"). Render them as clickable
                    thumbnails above the textarea so admins never have to copy-paste from raw text. */}
                {(() => {
                  const urls = extractImageUrlsFromText(draft.notes || "");
                  if (urls.length === 0) return null;
                  return (
                    <div className="mb-2 flex flex-wrap gap-2" data-testid="bo-notes-photos">
                      {urls.map((u, i) => {
                        const resolved = resolveBackorderPhotoUrl(u);
                        return (
                          <a
                            key={i}
                            href={resolved}
                            target="_blank"
                            rel="noreferrer"
                            title={`Open ${u.split("/").pop()} in a new tab`}
                            className="group relative block"
                            data-testid={`bo-notes-photo-${i}`}
                          >
                            <img
                              src={resolved}
                              alt="Attached by requestor"
                              className="w-20 h-20 rounded object-cover bg-slate-100 border border-slate-200 cursor-zoom-in group-hover:ring-2 group-hover:ring-slate-400"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                const sibling = e.currentTarget.nextElementSibling;
                                if (sibling) sibling.style.display = "flex";
                              }}
                            />
                            <div
                              style={{ display: "none" }}
                              className="w-20 h-20 rounded border border-red-200 bg-red-50 items-center justify-center text-[9px] text-red-600 font-bold text-center px-1"
                            >
                              Photo unavailable
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  );
                })()}
                <Textarea
                  disabled={!editing}
                  rows={3}
                  value={draft.notes || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                  className="text-sm disabled:bg-slate-50"
                />
              </section>

              {/* Admin internal notes */}
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Admin notes (internal)</h3>
                <Textarea
                  rows={3}
                  value={draft.admin_notes || ""}
                  onChange={(e) => setDraft((d) => ({ ...d, admin_notes: e.target.value }))}
                  className="text-sm"
                  placeholder="Internal-only notes (never shown on supplier RFQ or hospital emails)"
                />
              </section>

              {/* Requestor contact */}
              <section className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded p-3">
                <div><strong>Requestor:</strong> {backorder.contact_name || "—"} &lt;{backorder.contact_email || "—"}&gt;</div>
                <div><strong>Phone:</strong> {backorder.contact_phone || "—"}</div>
              </section>

              {editing && (
                <div className="flex justify-end gap-2 sticky bottom-0 bg-white pt-3 border-t border-slate-200">
                  <Button variant="outline" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
                  <Button onClick={saveEdits} disabled={saving} className="bg-amber-600 hover:bg-amber-700">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : null}
                    Save changes
                  </Button>
                </div>
              )}
            </div>
          )}

          {tab === "quotes" && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs text-slate-500">
                  {quotes.length === 0
                    ? "No quotes yet for this backorder."
                    : `${quotes.length} quote${quotes.length === 1 ? "" : "s"} received.`}
                </p>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowManualQuote(true)}
                  className="gap-1.5 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                  data-testid="bo-drawer-add-manual-quote"
                  title="Log a quote that came in by email or phone"
                >
                  <Plus className="w-3.5 h-3.5" /> Enter Quote Manually
                </Button>
              </div>
              {quotes.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-10">
                  No quotes yet. Click <strong>Source from Suppliers &amp; Contractors</strong> above to broadcast this RFQ,
                  or use <strong>Enter Quote Manually</strong> to log one that came in by phone / email.
                </p>
              ) : (
                <>
                  <div className="flex items-center justify-end gap-2 mb-3">
                    <span className="text-xs text-slate-500">View:</span>
                    <button onClick={() => setQuoteView("cards")} className={`text-xs px-2 py-1 rounded ${quoteView === "cards" ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-700"}`}>Cards</button>
                    <button onClick={() => setQuoteView("table")} className={`text-xs px-2 py-1 rounded ${quoteView === "table" ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-700"}`}>Table</button>
                  </div>
                  {quoteView === "table" ? (
                    <div className="overflow-x-auto border border-slate-200 rounded-md">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                          <tr>
                            <th className="px-3 py-2 text-left">Supplier</th>
                            <th className="px-3 py-2 text-right">Total ($)</th>
                            <th className="px-3 py-2 text-left">Lead time</th>
                            <th className="px-3 py-2 text-right">Freight</th>
                            <th className="px-3 py-2 text-left">Notes</th>
                            <th className="px-3 py-2 text-right w-20">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {quotes
                            .slice()
                            .sort((a, b) => (a.total_estimate || 9e9) - (b.total_estimate || 9e9))
                            .map((q) => (
                            <tr key={q.id} className="hover:bg-amber-50/40" data-testid={`quote-row-${q.id}`}>
                              <td className="px-3 py-2 font-semibold text-slate-900">{q.supplier_name}</td>
                              <td className="px-3 py-2 text-right">{q.total_estimate ? `$${q.total_estimate.toFixed(2)}` : "—"}</td>
                              <td className="px-3 py-2">{q.lead_time || "—"}</td>
                              <td className="px-3 py-2 text-right">{q.freight_estimate ? `$${q.freight_estimate.toFixed(2)}` : (backorder.use_hospital_courier ? "n/a" : "—")}</td>
                              <td className="px-3 py-2 text-slate-600 line-clamp-2">{q.notes || "—"}</td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">
                                <button
                                  type="button"
                                  onClick={() => setEditingQuote(q)}
                                  className="text-amber-700 hover:text-amber-900 mr-2"
                                  title="Edit quote"
                                  data-testid={`quote-edit-${q.id}`}
                                >
                                  <Edit3 className="w-3.5 h-3.5 inline" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteQuote(q)}
                                  className="text-rose-600 hover:text-rose-800"
                                  title="Delete quote"
                                  data-testid={`quote-delete-${q.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5 inline" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-3">
                      {quotes.map((q) => (
                        <div key={q.id} className="rounded-lg border border-slate-200 p-3 bg-white" data-testid={`quote-card-${q.id}`}>
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h4 className="font-bold text-sm text-slate-900">{q.supplier_name}</h4>
                              <p className="text-[11px] text-slate-500">{q.supplier_email}</p>
                            </div>
                            <span className="text-lg font-extrabold text-emerald-700">
                              {q.total_estimate ? `$${q.total_estimate.toFixed(2)}` : "—"}
                            </span>
                          </div>
                          <div className="text-xs text-slate-700 space-y-1">
                            <div><strong>Lead time:</strong> {q.lead_time || "—"}</div>
                            {q.freight_estimate != null && <div><strong>Freight:</strong> ${q.freight_estimate.toFixed(2)}</div>}
                            {q.notes && <div className="text-slate-600 line-clamp-3 pt-1 border-t border-slate-100">{q.notes}</div>}
                          </div>
                          <div className="flex justify-end gap-2 mt-3 pt-2 border-t border-slate-100">
                            <button
                              type="button"
                              onClick={() => {
                                setAcceptedQuote(q);
                                setTab("build");
                                toast.success(`Quote from ${q.supplier_name} accepted`, {
                                  description: "Switched to Build Quote — supplier costs prefilled with current markup %.",
                                });
                              }}
                              className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1 mr-auto"
                              title="Accept this quote → switch to Build Quote tab and prefill supplier costs"
                              data-testid={`quote-accept-${q.id}`}
                            >
                              <CheckCircle2 className="w-3 h-3" /> Accept &amp; Build Hospital Quote
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingQuote(q)}
                              className="text-[11px] font-semibold text-amber-700 hover:text-amber-900 inline-flex items-center gap-1"
                              data-testid={`quote-edit-${q.id}`}
                            >
                              <Edit3 className="w-3 h-3" /> Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => deleteQuote(q)}
                              className="text-[11px] font-semibold text-rose-600 hover:text-rose-800 inline-flex items-center gap-1"
                              data-testid={`quote-delete-${q.id}`}
                            >
                              <Trash2 className="w-3 h-3" /> Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {tab === "build" && (
            <BackorderCustomerQuoteTab
              backorder={backorder}
              token={token}
              onSent={onRefresh}
              prefillFromQuote={acceptedQuote}
              onPrefillConsumed={() => setAcceptedQuote(null)}
            />
          )}

          {tab === "order" && (
            <BackorderCustomerOrderTab
              backorder={backorder}
              token={token}
              onRefresh={onRefresh}
              onOpenOrder={onOpenOrder}
            />
          )}

          {tab === "fulfillment" && (
            <BackorderFulfillmentTab
              backorder={backorder}
              token={token}
              onRefresh={onRefresh}
            />
          )}

          {tab === "payables" && (
            <SupplierPayablesPanel
              linkType="backorder"
              linkId={backorder.id}
              token={token}
              defaultSupplier={{
                name: backorder.awarded_supplier_name,
                email: backorder.awarded_supplier_email,
              }}
            />
          )}

          {tab === "trade" && (
            <BackorderTradeIntelTab backorder={backorder} token={token} />
          )}

          {tab === "log" && (
            <div>
              {(backorder.sourcing_broadcasts || []).length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-10">No broadcasts yet.</p>
              ) : (
                <ul className="space-y-3">
                  {(backorder.sourcing_broadcasts || []).slice().reverse().map((b) => (
                    <li key={b.id} className="rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-bold text-slate-900">{b.subject}</span>
                        <span className="text-[11px] text-slate-500">{fmtAge(b.broadcast_at)}</span>
                      </div>
                      <p className="text-xs text-slate-600 mb-2">By {b.broadcast_by} · {b.recipient_count} supplier{b.recipient_count === 1 ? "" : "s"} BCC'd</p>
                      <details className="text-xs text-slate-500">
                        <summary className="cursor-pointer">Show recipients</summary>
                        <ul className="mt-1 ml-3 list-disc">
                          {(b.recipients || []).map((r, i) => (
                            <li key={i}>{r.name} · {r.email}</li>
                          ))}
                        </ul>
                      </details>
                    </li>
                  ))}
                </ul>
              )}

              {/* Admin audit log (status changes / edits) */}
              {(backorder.admin_audit_log || []).length > 0 && (
                <>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mt-6 mb-2">Admin actions</h4>
                  <ul className="space-y-2 text-xs text-slate-600">
                    {(backorder.admin_audit_log || []).slice().reverse().map((a) => (
                      <li key={a.id} className="border-l-2 border-amber-300 pl-2">
                        <span className="font-semibold text-slate-700">{a.by}</span> · {fmtAge(a.at)}
                        <code className="block bg-slate-50 mt-0.5 px-2 py-0.5 rounded">{JSON.stringify(a.changes)}</code>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {showSourcing && (
        <SourceFromSuppliersModal
          backorderId={backorder.id}
          token={token}
          onClose={() => setShowSourcing(false)}
          onBroadcast={() => { onRefresh?.(); }}
        />
      )}

      {showDiPo && (
        <DIPoDispatchDialog
          backorderId={backorder.id}
          token={token}
          onClose={() => setShowDiPo(false)}
          onSent={() => { onRefresh?.(); }}
        />
      )}

      {showManualQuote && (
        <ManualQuoteEntryDialog
          backorder={backorder}
          token={token}
          onClose={() => setShowManualQuote(false)}
          onSaved={() => { setShowManualQuote(false); reloadQuotes(); onRefresh?.(); }}
        />
      )}

      {editingQuote && (
        <ManualQuoteEntryDialog
          backorder={backorder}
          token={token}
          existingQuote={editingQuote}
          onClose={() => setEditingQuote(null)}
          onSaved={() => { setEditingQuote(null); reloadQuotes(); onRefresh?.(); }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Inline "New Backorder" modal (simpler wrapper around /api/order-request)
// ============================================================================
function NewBackorderInline({ open, onClose, onSuccess, token, directOrder = false }) {
  const { user } = useAuth();
  const canCreateHistorical = !directOrder && (user?.role === "super_admin" || user?.role === "admin_accountant");
  const [submitting, setSubmitting] = useState(false);
  // Order type — "backorder" for standard medical supplies, "maintenance_repair"
  // for facility maintenance & repair work orders (parts + labor + travel,
  // sourced from contractors and/or suppliers).
  const [orderType, setOrderType] = useState("backorder");
  const [items, setItems] = useState([{ item_type: "part", name: "", sku: "", quantity: 1, uom: "Each", notes: "", substitute: null, hours: "", hourly_rate: "", miles: "", per_mile_rate: "", flat_fee: "" }]);
  const [meta, setMeta] = useState({ hospital_name: "", notes: "" });
  const [ship, setShip] = useState({ city: "", state: "", zip: "" });
  const [useCourier, setUseCourier] = useState(false);
  const [courier, setCourier] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [picked, setPicked] = useState(null);    // {account_id, account_name, email, name, ...}
  const [pickedContactEmail, setPickedContactEmail] = useState("");
  const [pickedContactName, setPickedContactName] = useState("");

  // Hospital typeahead — loads full hospital list once when the modal opens.
  // Filters as the admin types so they don't have to remember the exact name.
  const [hospitalDb, setHospitalDb] = useState([]);
  const [showHospitalSuggest, setShowHospitalSuggest] = useState(false);
  useEffect(() => {
    if (!open || picked) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/backorders/hospitals`, { headers: { Authorization: `Bearer ${token}` } });
        if (!cancelled) {
          const list = Array.isArray(r.data) ? r.data : (r.data?.hospitals || r.data?.items || []);
          setHospitalDb(list);
        }
      } catch (_e) { /* non-fatal — picker still works */ }
    })();
    return () => { cancelled = true; };
  }, [open, picked, token]);

  const hospitalMatches = (() => {
    const q = (meta.hospital_name || "").trim().toLowerCase();
    if (!q || picked) return [];
    return hospitalDb
      .filter((h) => (h.hospital_name || "").toLowerCase().includes(q))
      .slice(0, 8);
  })();

  // Historical backfill mode — surface only for super_admin / admin_accountant.
  // Reshapes the form to capture closed-order data without firing any emails.
  const [historical, setHistorical] = useState(false);
  const [hist, setHist] = useState({
    record_status: "fulfilled",
    original_order_date: "",
    closed_date: "",
    awarded_supplier_name: "",
    tracking_number: "",
    tracking_courier: "UPS",
    invoice_number: "",
    invoice_total_billed: "",
    invoice_sent_at: "",
    customer_po_number: "",
    date_shipped: "",
    freight_cost: "",
    payment_terms: "Net 30",
  });

  // Direct-order mode ("New Order" button) — bypasses the RFQ entirely.
  const [direct, setDirect] = useState({ po_number: "", awarded_supplier_name: "" });
  const showPricing = historical || directOrder;

  if (!open) return null;

  const addItem = () => setItems((p) => [...p, { item_type: orderType === "maintenance_repair" ? "part" : "part", name: "", sku: "", quantity: 1, uom: "Each", unit_cost: "", notes: "", substitute: null, hours: "", hourly_rate: "", miles: "", per_mile_rate: "", flat_fee: "" }]);
  const removeItem = (i) => setItems((p) => p.filter((_, idx) => idx !== i));
  const setIt = (i, k, v) => setItems((p) => p.map((it, idx) => idx === i ? { ...it, [k]: v } : it));

  const submit = async (e) => {
    e.preventDefault();
    const valid = items.filter((it) => (it.name || "").trim());
    if (valid.length === 0) { toast.error("Add at least one item"); return; }
    if (!meta.hospital_name.trim()) { toast.error("Hospital name required (internal reference)"); return; }
    setSubmitting(true);

    // Normalize a single line item — keeps backorder rows lean and merges
    // M&R-specific fields (hours/rate/miles/flat) when applicable. Historical
    // mode also captures unit_cost for transaction-record export.
    const buildItem = (it) => {
      const base = {
        name: it.name,
        sku: (it.sku || "").trim() || null,
        quantity: parseInt(it.quantity) || 1,
        uom: it.uom || "Each",
        notes: it.notes || "",
        substitute: it.substitute || null,
      };
      // Carry unit_cost on historical (or any) line if provided — used for the
      // emailed transaction record / receipt.
      if (it.unit_cost !== "" && it.unit_cost != null) {
        base.unit_cost = parseFloat(it.unit_cost);
      }
      if (orderType !== "maintenance_repair") return base;
      const itype = it.item_type || "part";
      base.item_type = itype;
      if (itype === "labor") {
        base.hours = it.hours === "" || it.hours == null ? null : parseFloat(it.hours);
        base.hourly_rate = it.hourly_rate === "" || it.hourly_rate == null ? null : parseFloat(it.hourly_rate);
      } else if (itype === "travel") {
        base.miles = it.miles === "" || it.miles == null ? null : parseFloat(it.miles);
        base.per_mile_rate = it.per_mile_rate === "" || it.per_mile_rate == null ? null : parseFloat(it.per_mile_rate);
        base.flat_fee = it.flat_fee === "" || it.flat_fee == null ? null : parseFloat(it.flat_fee);
      }
      return base;
    };

    try {
      // Direct order — create the live order (RFQ bypassed) and seed its
      // Build Order doc with the customer PO + priced line items.
      if (directOrder) {
        if (!direct.po_number.trim()) {
          toast.error("Customer PO # is required for a direct order");
          setSubmitting(false);
          return;
        }
        const shipAddr = [ship.city, ship.state, ship.zip].map((s) => s.trim()).filter(Boolean).join(", ");
        const r = await axios.post(`${API}/api/admin/backorders/direct-order`, {
          order_type: orderType,
          hospital_name: meta.hospital_name,
          hospital_id: picked?.account_id || undefined,
          contact_name: pickedContactName || picked?.name || "—",
          contact_email: pickedContactEmail || picked?.email || "",
          crm_account_id: picked?.account_id || undefined,
          crm_contact_id: picked?.contact_id || undefined,
          items: valid.map(buildItem),
          po_number: direct.po_number.trim(),
          awarded_supplier_name: direct.awarded_supplier_name.trim() || null,
          notes: meta.notes || null,
          shipping_address: shipAddr,
          use_hospital_courier: useCourier,
          hospital_courier_account: useCourier ? courier : "",
          ship_via: useCourier ? "Hospital 3rd-Party Courier" : "Best Way",
          terms: "Net 30",
        }, { headers: { Authorization: `Bearer ${token}` } });

        const newId = r.data?.id;
        // Seed the Build Order tab so the PO + pricing are ready to send.
        try {
          await axios.post(`${API}/api/admin/backorders/${newId}/customer-order`, {
            po_number: direct.po_number.trim(),
            line_items: valid.map((it) => ({
              name: it.name,
              sku: (it.sku || "").trim() || null,
              quantity: parseInt(it.quantity) || 1,
              uom: it.uom || "Each",
              unit_price: parseFloat(it.unit_cost) || 0,
              notes: it.notes || null,
            })),
            use_hospital_courier: useCourier,
            hospital_courier_account: useCourier ? courier : null,
            ship_via: useCourier ? "Hospital 3rd-Party Courier" : "Best Way",
            terms: "Net 30",
            status: "draft",
          }, { headers: { Authorization: `Bearer ${token}` } });
        } catch (_e) { /* order exists either way — Build Order tab can be filled manually */ }

        toast.success(`Order ${newId} created — PO ${direct.po_number.trim()}`, {
          description: "No RFQ sent. Opening the order so you can confirm and ship it.",
        });
        onSuccess?.(newId);
        onClose();
        return;
      }

      // Historical backfill mode — write directly to backorder_requests with
      // is_historical=true. Bypasses orders email pipeline entirely.
      if (historical) {
        const r = await axios.post(`${API}/api/admin/backorders/historical`, {
          hospital_name: meta.hospital_name,
          hospital_id: picked?.account_id || undefined,
          contact_email: pickedContactEmail || picked?.email || "historical@sterilemate.com",
          contact_name: pickedContactName || picked?.name || "—",
          items: valid.map(buildItem),
          order_type: orderType,
          record_status: hist.record_status,
          original_order_date: hist.original_order_date || null,
          closed_date: hist.closed_date || null,
          awarded_supplier_name: hist.awarded_supplier_name || null,
          tracking_number: hist.tracking_number || null,
          courier: hist.tracking_number ? hist.tracking_courier : null,
          invoice_number: hist.invoice_number || null,
          invoice_total_billed: hist.invoice_total_billed === "" ? null : hist.invoice_total_billed,
          invoice_sent_at: hist.invoice_sent_at || null,
          customer_po_number: hist.customer_po_number || null,
          date_shipped: hist.date_shipped || null,
          freight_cost: hist.freight_cost === "" ? null : hist.freight_cost,
          payment_terms: hist.payment_terms || null,
          notes: meta.notes || null,
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Historical record added — no notifications sent.");
        onSuccess?.();
        onClose();
        return;
      }
      const shipAddr = [ship.city, ship.state, ship.zip].map((s) => s.trim()).filter(Boolean).join(", ");
      const r = await axios.post(`${API}/api/order-request`, {
        order_type: orderType,  // "backorder" or "maintenance_repair"
        hospital_name: meta.hospital_name,
        address: shipAddr,
        contact_name: pickedContactName || picked?.name || "Sterile Mate Admin",
        contact_email: pickedContactEmail || picked?.email || "orders@sterilemate.com",
        contact_phone: "",
        crm_account_id: picked?.account_id || undefined,
        crm_contact_id: picked?.contact_id || undefined,
        item_name: valid.map((it) => it.name).join(", "),
        mfr_number: valid.map((it) => (it.sku || "").trim()).filter(Boolean).join(", ") || null,
        quantity: valid.reduce((s, it) => s + (parseInt(it.quantity) || 1), 0),
        notes: meta.notes,
        items: valid.map(buildItem),
        shipping_address: shipAddr,
        use_hospital_courier: useCourier,
        hospital_courier_account: useCourier ? courier : "",
        ship_via: useCourier ? "Hospital 3rd-Party Courier" : "Best Way",
        terms: "Net 30",
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(orderType === "maintenance_repair" ? "Maintenance & Repair work order created" : "Backorder created");
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-2xl w-full my-8 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-slate-900">
            {directOrder
              ? (orderType === "maintenance_repair"
                  ? "New Maintenance & Repair Order (Direct)"
                  : "New Order — Direct (skips the RFQ)")
              : historical
              ? "Historical Backorder Record"
              : orderType === "maintenance_repair"
                ? "New Maintenance & Repair Work Order"
                : "New Backorder Request (Manual)"}
          </h2>
          <button onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>

        {/* Order type toggle — Backorder vs Maintenance & Repair. M&R unlocks
            labor & travel line items and contractor sourcing. Disabled when
            historical mode is on (historical mode is supply-only). */}
        {!historical && (
          <div className="mb-4 rounded-lg border-2 border-amber-200 bg-amber-50/40 p-3" data-testid="bo-form-order-type">
            <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Order type</div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setOrderType("backorder")}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-bold border transition-colors ${
                  orderType === "backorder"
                    ? "bg-amber-600 text-white border-amber-600"
                    : "bg-white text-slate-700 border-slate-200 hover:border-amber-300"
                }`}
                data-testid="bo-form-order-type-backorder"
              >
                <span className="block">Backorder</span>
                <span className="block text-[10px] font-normal opacity-80 mt-0.5">Medical supply RFQ to suppliers</span>
              </button>
              <button
                type="button"
                onClick={() => setOrderType("maintenance_repair")}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-bold border transition-colors ${
                  orderType === "maintenance_repair"
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-slate-700 border-slate-200 hover:border-indigo-300"
                }`}
                data-testid="bo-form-order-type-mr"
              >
                <span className="block">Maintenance &amp; Repair</span>
                <span className="block text-[10px] font-normal opacity-80 mt-0.5">Parts + labor + travel · contractors</span>
              </button>
            </div>
          </div>
        )}

        {/* Direct-order header block — customer PO is mandatory so it can
            flow into the order confirmation + fulfillment emails. */}
        {directOrder && (
          <div className="mb-4 rounded-lg border-2 border-emerald-200 bg-emerald-50/50 p-3" data-testid="bo-form-direct-block">
            <div className="text-[11px] text-emerald-900 mb-2">
              <strong>Direct order</strong> — no RFQ is sent to suppliers. This lands in the desk as
              <strong> Awarded</strong>, ready for Build Order &amp; Fulfillment.
            </div>
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-12 sm:col-span-6">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-0.5">Customer PO # *</label>
                <Input
                  placeholder="MH-PO-12345"
                  className="h-9"
                  value={direct.po_number}
                  onChange={(e) => setDirect((d) => ({ ...d, po_number: e.target.value }))}
                  data-testid="bo-form-direct-po"
                />
              </div>
              <div className="col-span-12 sm:col-span-6">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-0.5">Supplier (optional)</label>
                <Input
                  placeholder="Who are we buying from?"
                  className="h-9"
                  value={direct.awarded_supplier_name}
                  onChange={(e) => setDirect((d) => ({ ...d, awarded_supplier_name: e.target.value }))}
                  data-testid="bo-form-direct-supplier"
                />
              </div>
            </div>
          </div>
        )}

        {/* Historical mode toggle (super_admin / admin_accountant only) */}
        {canCreateHistorical && (
          <div className={`mb-4 rounded-lg border-2 p-3 flex items-center justify-between gap-3 ${
            historical ? "border-slate-700 bg-slate-100" : "border-slate-200 bg-slate-50"
          }`}>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={historical}
                onChange={(e) => setHistorical(e.target.checked)}
                className="mt-1 w-4 h-4 accent-slate-700"
                data-testid="bo-form-historical-toggle"
              />
              <div>
                <div className="text-sm font-bold text-slate-800">Historical record (already fulfilled)</div>
                <div className="text-[11px] text-slate-500">
                  Backfill an order that was completed in the past. <strong>No emails or notifications will be sent</strong> — supplier, hospital, and milestone alerts are all suppressed.
                </div>
              </div>
            </label>
            {historical && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase text-amber-800 bg-amber-100 border border-amber-200 rounded px-2 py-0.5 shrink-0">
                <FileText className="w-3 h-3" /> Record-only
              </span>
            )}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Items</p>
            <div className="space-y-3">
              {items.map((it, i) => {
                const isMR = orderType === "maintenance_repair";
                const itype = it.item_type || "part";
                return (
                <div key={i} className="rounded border border-slate-200 bg-slate-50/60 p-2 space-y-1.5">
                  {/* M&R line type selector — Part / Labor / Travel. Only
                      shown when the order type is maintenance_repair. */}
                  {isMR && (
                    <div className="flex items-center gap-1.5" data-testid={`bo-form-item-type-${i}`}>
                      <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mr-1">Type</span>
                      {[
                        { key: "part", label: "Part" },
                        { key: "labor", label: "Labor" },
                        { key: "travel", label: "Travel" },
                      ].map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setIt(i, "item_type", opt.key)}
                          className={`px-2 py-0.5 rounded text-[11px] font-bold transition-colors ${
                            itype === opt.key
                              ? "bg-indigo-600 text-white"
                              : "bg-white text-slate-600 border border-slate-200 hover:border-indigo-300"
                          }`}
                          data-testid={`bo-form-item-type-${i}-${opt.key}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="grid grid-cols-12 gap-2">
                    <Input placeholder={
                      isMR && itype === "labor" ? `Labor description ${i + 1} *` :
                      isMR && itype === "travel" ? `Travel description ${i + 1} *` :
                      `Item ${i + 1} *`
                    } className="col-span-12 sm:col-span-5 h-9" value={it.name} onChange={(e) => setIt(i, "name", e.target.value)} data-testid={`bo-form-item-name-${i}`} />
                    {!isMR || itype === "part" ? (
                      <>
                        <Input placeholder="SKU / Part #" className="col-span-6 sm:col-span-3 h-9 font-mono text-xs" value={it.sku || ""} onChange={(e) => setIt(i, "sku", e.target.value)} data-testid={`bo-form-item-sku-${i}`} />
                        <Input type="number" min="1" placeholder="Qty" className="col-span-3 sm:col-span-2 h-9" value={it.quantity} onChange={(e) => setIt(i, "quantity", e.target.value)} data-testid={`bo-form-item-qty-${i}`} />
                        <select
                          className="col-span-3 sm:col-span-2 h-9 rounded border border-slate-200 bg-white text-sm px-2 focus:outline-none focus:ring-2 focus:ring-amber-400"
                          value={it.uom || "Each"}
                          onChange={(e) => setIt(i, "uom", e.target.value)}
                          data-testid={`bo-form-item-uom-${i}`}
                          aria-label="Unit of measure"
                        >
                          <option value="Each">Each</option>
                          <option value="Case">Case</option>
                          <option value="Box">Box</option>
                          <option value="Pack">Pack</option>
                          <option value="Pair">Pair</option>
                          <option value="Bag">Bag</option>
                          <option value="Roll">Roll</option>
                          <option value="Bottle">Bottle</option>
                          <option value="Kit">Kit</option>
                          <option value="Pallet">Pallet</option>
                        </select>
                        {showPricing && (
                          <div className="col-span-12 grid grid-cols-12 gap-2 mt-1">
                            <div className="col-span-12 sm:col-span-6">
                              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-0.5">{directOrder ? "Unit price ($)" : "Unit cost ($)"}</label>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0.00"
                                className="h-8 text-sm text-right"
                                value={it.unit_cost || ""}
                                onChange={(e) => setIt(i, "unit_cost", e.target.value)}
                                data-testid={`bo-form-item-unit-cost-${i}`}
                              />
                            </div>
                            <div className="col-span-12 sm:col-span-6">
                              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-0.5">Line total</label>
                              <div className="h-8 px-2 flex items-center justify-end rounded border border-slate-200 bg-slate-100 text-sm font-bold text-slate-800" data-testid={`bo-form-item-line-total-${i}`}>
                                {it.unit_cost && it.quantity
                                  ? `$${(parseFloat(it.unit_cost) * (parseInt(it.quantity) || 0)).toFixed(2)}`
                                  : "—"}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : itype === "labor" ? (
                      <>
                        <Input type="number" step="0.25" min="0" placeholder="Hours" className="col-span-6 sm:col-span-3 h-9" value={it.hours} onChange={(e) => setIt(i, "hours", e.target.value)} data-testid={`bo-form-item-hours-${i}`} />
                        <Input type="number" step="0.01" min="0" placeholder="$ / hr" className="col-span-6 sm:col-span-4 h-9" value={it.hourly_rate} onChange={(e) => setIt(i, "hourly_rate", e.target.value)} data-testid={`bo-form-item-hourly-rate-${i}`} />
                      </>
                    ) : (
                      <>
                        <Input type="number" step="0.1" min="0" placeholder="Miles" className="col-span-4 sm:col-span-2 h-9" value={it.miles} onChange={(e) => setIt(i, "miles", e.target.value)} data-testid={`bo-form-item-miles-${i}`} />
                        <Input type="number" step="0.01" min="0" placeholder="$ / mi" className="col-span-4 sm:col-span-2 h-9" value={it.per_mile_rate} onChange={(e) => setIt(i, "per_mile_rate", e.target.value)} data-testid={`bo-form-item-per-mile-${i}`} />
                        <Input type="number" step="0.01" min="0" placeholder="Or flat $" className="col-span-4 sm:col-span-3 h-9" value={it.flat_fee} onChange={(e) => setIt(i, "flat_fee", e.target.value)} data-testid={`bo-form-item-flat-fee-${i}`} />
                      </>
                    )}
                  </div>
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 block mb-0.5">{
                      isMR && itype === "labor" ? "Scope of work / notes for the contractor"
                      : isMR && itype === "travel" ? "Travel notes (origin, vehicle, per-diem, etc.)"
                      : "Item description / notes for the supplier"
                    }</label>
                    <textarea
                      placeholder={
                        isMR && itype === "labor"
                          ? "Detailed scope: e.g. troubleshoot RO loop pump, replace bearing, full system flush + bacterial test."
                          : isMR && itype === "travel"
                          ? "Origin city, expected drive time, lodging needed, fuel surcharge, etc."
                          : "Anything the supplier needs to source the right item: lot, color, sub-config, brand preference, end-use department, urgency, alternate SKUs we've seen substituted, etc."
                      }
                      rows={3}
                      className="w-full rounded border border-slate-200 bg-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400 resize-y min-h-[68px]"
                      value={it.notes}
                      onChange={(e) => setIt(i, "notes", e.target.value)}
                      data-testid={`bo-form-item-notes-${i}`}
                    />
                  </div>
                  <div className="flex items-center gap-2 pl-1">
                    {!isMR && (it.substitute ? (
                      <div className="flex-1 grid grid-cols-12 gap-2 items-center bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                        <span className="col-span-12 sm:col-span-1 text-[10px] font-bold uppercase tracking-wider text-amber-700">Sub →</span>
                        <Input
                          placeholder="Substitute name"
                          className="col-span-12 sm:col-span-5 h-8 text-xs bg-white"
                          value={it.substitute.name || ""}
                          onChange={(e) => setIt(i, "substitute", { ...(it.substitute || {}), name: e.target.value })}
                          data-testid={`bo-form-item-sub-name-${i}`}
                        />
                        <Input
                          placeholder="Substitute SKU"
                          className="col-span-8 sm:col-span-3 h-8 text-xs bg-white font-mono"
                          value={it.substitute.sku || ""}
                          onChange={(e) => setIt(i, "substitute", { ...(it.substitute || {}), sku: e.target.value })}
                          data-testid={`bo-form-item-sub-sku-${i}`}
                        />
                        <Input
                          placeholder="Why (optional)"
                          className="col-span-4 sm:col-span-2 h-8 text-xs bg-white"
                          value={it.substitute.notes || ""}
                          onChange={(e) => setIt(i, "substitute", { ...(it.substitute || {}), notes: e.target.value })}
                        />
                        <button
                          type="button"
                          onClick={() => setIt(i, "substitute", null)}
                          className="col-span-12 sm:col-span-1 text-rose-500 hover:text-rose-700 text-xs"
                          data-testid={`bo-form-item-sub-remove-${i}`}
                          title="Remove substitute"
                        >Remove</button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setIt(i, "substitute", { name: "", sku: "", notes: "" })}
                        className="text-[11px] font-semibold text-amber-700 hover:text-amber-800 inline-flex items-center gap-1"
                        data-testid={`bo-form-item-add-sub-${i}`}
                      >
                        + Add substitute option
                      </button>
                    ))}
                    {items.length > 1 && <button type="button" onClick={() => removeItem(i)} className="ml-auto text-slate-400 hover:text-rose-600 text-sm">✕</button>}
                  </div>
                </div>
                );
              })}
              <button type="button" onClick={addItem} className="text-xs font-semibold text-amber-700 hover:text-amber-800">+ Add another item</button>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Hospital / Lead</p>
              <Button type="button" size="sm" variant="outline" onClick={() => setPickerOpen(true)} className="gap-1.5 h-7 text-xs" data-testid="bo-form-pick-crm">
                <Users className="w-3 h-3" /> Pick from CRM…
              </Button>
            </div>
            {picked ? (
              <div className="rounded border border-emerald-300 bg-emerald-50/60 p-2 flex items-start justify-between gap-2">
                <div className="text-sm">
                  <div className="font-bold text-slate-900">{picked.account_name}</div>
                  <div className="text-xs text-slate-600">{pickedContactName || picked.name || ""} · {pickedContactEmail || picked.email}</div>
                  {picked.department && <div className="text-[10px] text-violet-700 mt-0.5 font-bold uppercase">{picked.department}</div>}
                </div>
                <button type="button" onClick={() => { setPicked(null); setMeta({ ...meta, hospital_name: "" }); }} className="text-rose-500 hover:text-rose-700">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Input
                    placeholder="Hospital / facility name * — start typing to search existing customers"
                    className="h-9"
                    value={meta.hospital_name}
                    onChange={(e) => { setMeta({ ...meta, hospital_name: e.target.value }); setShowHospitalSuggest(true); }}
                    onFocus={() => setShowHospitalSuggest(true)}
                    onBlur={() => setTimeout(() => setShowHospitalSuggest(false), 180)}
                    autoComplete="off"
                    data-testid="bo-form-hospital-name"
                  />
                  {showHospitalSuggest && hospitalMatches.length > 0 && (
                    <ul
                      className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-md shadow-xl max-h-64 overflow-y-auto"
                      data-testid="bo-form-hospital-suggest"
                    >
                      {hospitalMatches.map((h, idx) => (
                        <li key={`${h.account_id || h.hospital_name}-${idx}`}>
                          <button
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); }} // keep focus
                            onClick={() => {
                              setPicked({
                                account_id: h.account_id || null,
                                account_name: h.hospital_name,
                                email: h.contact_email || "",
                                name: h.contact_name || "",
                              });
                              setMeta((m) => ({ ...m, hospital_name: h.hospital_name }));
                              setPickedContactEmail(h.contact_email || "");
                              setPickedContactName(h.contact_name || "");
                              setShowHospitalSuggest(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-amber-50 text-sm border-b border-slate-100 last:border-b-0 flex items-start justify-between gap-3"
                            data-testid={`bo-form-hospital-suggest-${idx}`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="font-medium text-slate-900 truncate">{h.hospital_name}</div>
                              <div className="text-[11px] text-slate-500 truncate">
                                {h.contact_name || "—"} · {h.contact_email || "no email on file"}
                              </div>
                            </div>
                            {h.account_id && (
                              <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 shrink-0 mt-0.5">
                                CRM
                              </span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <p className="text-[11px] text-slate-500 mt-1">
                  Type a name to auto-match existing customers · or <button type="button" onClick={() => setPickerOpen(true)} className="text-amber-700 font-bold underline">pick from CRM</button> · not in CRM? Add a new lead from the picker.
                </p>
              </>
            )}
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Ship-to (city/state/zip only)</p>
            <div className="grid grid-cols-12 gap-2">
              <Input placeholder="City" className="col-span-6 h-9" value={ship.city} onChange={(e) => setShip({ ...ship, city: e.target.value })} />
              <Input placeholder="ST" maxLength={2} className="col-span-2 h-9 uppercase" value={ship.state} onChange={(e) => setShip({ ...ship, state: e.target.value.toUpperCase().slice(0, 2) })} />
              <Input placeholder="ZIP" className="col-span-4 h-9" value={ship.zip} onChange={(e) => setShip({ ...ship, zip: e.target.value })} />
            </div>
          </div>
          <div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={useCourier} onChange={(e) => setUseCourier(e.target.checked)} className="accent-amber-600" />
              <strong>Use hospital 3rd-party courier for freight</strong>
            </label>
            {useCourier && (
              <Input placeholder="Courier & account #" className="h-9 mt-2" value={courier} onChange={(e) => setCourier(e.target.value)} />
            )}
          </div>
          <Textarea rows={3} placeholder={historical ? "Internal notes (admin-only audit text)" : "Notes (visible to suppliers on the RFQ)"} value={meta.notes} onChange={(e) => setMeta({ ...meta, notes: e.target.value })} />

          {/* Historical-only fields */}
          {historical && (
            <div className="rounded-lg border-2 border-slate-300 bg-slate-50 p-3 space-y-3" data-testid="bo-form-historical-fields">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-700">Historical details</p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-0.5">Record as</label>
                  <select
                    value={hist.record_status}
                    onChange={(e) => setHist({ ...hist, record_status: e.target.value })}
                    className="w-full h-8 text-sm border border-slate-300 rounded px-2 bg-white"
                    data-testid="hist-record-status"
                  >
                    <option value="fulfilled">Fulfilled</option>
                    <option value="shipped">Shipped</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-0.5">Original order date</label>
                  <Input type="date" className="h-8 text-sm" value={hist.original_order_date}
                    onChange={(e) => setHist({ ...hist, original_order_date: e.target.value })}
                    data-testid="hist-original-date" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-0.5">Closed date</label>
                  <Input type="date" className="h-8 text-sm" value={hist.closed_date}
                    onChange={(e) => setHist({ ...hist, closed_date: e.target.value })}
                    data-testid="hist-closed-date" />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-0.5">Awarded supplier (optional)</label>
                  <Input className="h-8 text-sm" placeholder="Lux Medical Group" value={hist.awarded_supplier_name}
                    onChange={(e) => setHist({ ...hist, awarded_supplier_name: e.target.value })}
                    data-testid="hist-supplier" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-0.5">Tracking (optional)</label>
                  <div className="flex gap-1">
                    <select
                      value={hist.tracking_courier}
                      onChange={(e) => setHist({ ...hist, tracking_courier: e.target.value })}
                      className="w-24 h-8 text-sm border border-slate-300 rounded px-2 bg-white"
                    >
                      {["UPS", "FedEx", "USPS", "DHL", "Freight"].map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <Input className="h-8 text-sm flex-1" placeholder="Tracking #" value={hist.tracking_number}
                      onChange={(e) => setHist({ ...hist, tracking_number: e.target.value })}
                      data-testid="hist-tracking" />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-0.5">Invoice #</label>
                  <Input className="h-8 text-sm font-mono" placeholder="INV-..." value={hist.invoice_number}
                    onChange={(e) => setHist({ ...hist, invoice_number: e.target.value })}
                    data-testid="hist-invoice-number" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-0.5">Total billed ($)</label>
                  <Input type="number" step="0.01" min="0" className="h-8 text-sm text-right" value={hist.invoice_total_billed}
                    onChange={(e) => setHist({ ...hist, invoice_total_billed: e.target.value })}
                    data-testid="hist-invoice-total" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-0.5">Invoice sent</label>
                  <Input type="date" className="h-8 text-sm" value={hist.invoice_sent_at}
                    onChange={(e) => setHist({ ...hist, invoice_sent_at: e.target.value })}
                    data-testid="hist-invoice-sent" />
                </div>
              </div>

              <div className="pt-2 border-t border-slate-300 mt-2">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-700 mb-2">Transaction details (for emailed record)</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-0.5">Customer PO #</label>
                    <Input
                      className="h-8 text-sm font-mono"
                      placeholder="PO-12345"
                      value={hist.customer_po_number}
                      onChange={(e) => setHist({ ...hist, customer_po_number: e.target.value })}
                      data-testid="hist-customer-po"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-0.5">Date shipped</label>
                    <Input
                      type="date"
                      className="h-8 text-sm"
                      value={hist.date_shipped}
                      onChange={(e) => setHist({ ...hist, date_shipped: e.target.value })}
                      data-testid="hist-date-shipped"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-0.5">Freight cost ($)</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      className="h-8 text-sm text-right"
                      placeholder="0.00"
                      value={hist.freight_cost}
                      onChange={(e) => setHist({ ...hist, freight_cost: e.target.value })}
                      data-testid="hist-freight-cost"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-0.5">Payment terms</label>
                    <select
                      className="w-full h-8 text-sm border border-slate-300 rounded px-2 bg-white"
                      value={hist.payment_terms}
                      onChange={(e) => setHist({ ...hist, payment_terms: e.target.value })}
                      data-testid="hist-payment-terms"
                    >
                      <option value="Net 30">Net 30</option>
                      <option value="Net 15">Net 15</option>
                      <option value="Net 45">Net 45</option>
                      <option value="Net 60">Net 60</option>
                      <option value="Due on Receipt">Due on Receipt</option>
                      <option value="Prepaid">Prepaid</option>
                      <option value="Credit Card">Credit Card</option>
                    </select>
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 mt-2 leading-relaxed">
                  These fields are used to render the emailable <strong>Transaction Record</strong>.
                  After saving, open the record and click <em>Email transaction record</em> to send
                  the ordering party a clean receipt.
                </p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} className={historical ? "bg-slate-700 hover:bg-slate-800" : "bg-amber-600 hover:bg-amber-700"} data-testid="bo-form-submit">
              {submitting && <Loader2 className="w-4 h-4 animate-spin mr-1.5" />}
              {historical ? "Save Historical Record" : "Create Backorder"}
            </Button>
          </div>
        </form>

        <CrmContactPicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          mode="single"
          title="Pick hospital / lead from CRM"
          onConfirm={(arr) => {
            const c = (arr && arr[0]) || null;
            if (!c) return;
            setPicked(c);
            setPickedContactEmail(c.email || "");
            setPickedContactName(c.name || "");
            setMeta((m) => ({ ...m, hospital_name: c.account_name || c.name || "" }));
          }}
        />
      </div>
    </div>
  );
}


// ============================================================================
// Trade Intel tab — embedded in BackorderDetailDrawer
// Pulls the first item name from the backorder, calls the cached search
// endpoint, and shows the top consignees. Deep-links to the full Trade
// Intelligence page for paste/refresh workflow.
// ============================================================================
function BackorderTradeIntelTab({ backorder, token }) {
  const headers = { Authorization: `Bearer ${token}` };
  const firstItem = (backorder.items || [])[0]?.name || "";
  const [query, setQuery] = useState(firstItem);
  const [loading, setLoading] = useState(false);
  const [snapshot, setSnapshot] = useState(null);

  useEffect(() => {
    setQuery((backorder.items || [])[0]?.name || "");
    setSnapshot(null);
  }, [backorder.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const lookup = async () => {
    const q = (query || "").trim();
    if (q.length < 3) {
      toast.error("Item name too short to search");
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(
        `${API}/api/trade-intel/search`,
        { query: q },
        { headers }
      );
      setSnapshot(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lookup failed");
    } finally {
      setLoading(false);
    }
  };

  const goToFullPage = () => {
    const url = `/admin/trade-intelligence?q=${encodeURIComponent(query || "")}`;
    window.open(url, "_blank", "noopener");
  };

  const top = (snapshot?.results || []).slice(0, 5);

  return (
    <div className="space-y-3">
      <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs text-slate-600">
        <div className="flex items-start gap-2">
          <Globe className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div>
            <strong className="text-slate-800">Trade Intelligence</strong> shows
            the US hospitals, GPOs and distributors that already import this
            product by sea — useful for finding alternate suppliers or seeing
            which competitors stock it.
          </div>
        </div>
      </div>

      <div className="flex gap-2">
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Product name to search"
          className="text-sm"
          data-testid="bo-trade-query"
        />
        <Button
          size="sm"
          onClick={lookup}
          disabled={loading || !query.trim()}
          className="bg-amber-600 hover:bg-amber-700 gap-1.5"
          data-testid="bo-trade-search"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          Lookup
        </Button>
      </div>

      {snapshot && (
        <div className="text-xs text-slate-500">
          {snapshot.from_cache ? <>Cached {snapshot.cache_age_hours}h ago · </> : null}
          {snapshot.result_count} consignee{snapshot.result_count === 1 ? "" : "s"}
        </div>
      )}

      {snapshot && top.length === 0 && (
        <div className="text-sm text-slate-500 text-center py-6 bg-slate-50 rounded border border-dashed border-slate-200">
          <AlertCircle className="w-6 h-6 mx-auto text-amber-400 mb-1.5" />
          No cached data for "{snapshot.query}". Open the full Trade Intelligence
          page to paste a fresh CSV export.
        </div>
      )}

      {top.length > 0 && (
        <ul className="space-y-2">
          {top.map((r, i) => (
            <li key={`${r.consignee_name}-${i}`} className="border border-slate-200 rounded p-2.5 bg-white">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="font-semibold text-sm text-slate-800">{r.consignee_name}</div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
                  {r.shipment_count} shipment{r.shipment_count === 1 ? "" : "s"}
                </span>
              </div>
              <div className="text-xs text-slate-600 mt-1 space-y-0.5">
                {(r.consignee_city || r.consignee_state) && (
                  <div>{[r.consignee_city, r.consignee_state].filter(Boolean).join(", ")}</div>
                )}
                {r.shippers?.length > 0 && <div><span className="text-slate-400">Shippers:</span> {r.shippers.slice(0, 3).join(", ")}</div>}
                {r.last_arrival && <div><span className="text-slate-400">Last:</span> {r.last_arrival}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Button
        variant="outline"
        size="sm"
        onClick={goToFullPage}
        className="w-full gap-1.5"
        data-testid="bo-trade-open-full"
      >
        <ExternalLink className="w-3.5 h-3.5" />
        Open full Trade Intelligence page
      </Button>
    </div>
  );
}


// PaidToggle — small badge + click-to-toggle for customer order is_paid.
// Used in the drawer toolbar for AP to mark Net-30 invoices paid until QBO
// sync (Phase 2) closes the loop automatically.
function PaidToggle({ backorder, token, onRefresh }) {
  const headers = { Authorization: `Bearer ${token}` };
  const [busy, setBusy] = useState(false);
  const isPaid = !!backorder.is_paid;

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await axios.patch(
        `${API}/api/admin/backorders/${backorder.id}/paid`,
        { is_paid: !isPaid },
        { headers }
      );
      toast.success(isPaid ? "Marked unpaid" : "Marked paid");
      onRefresh?.();
    } catch (e) {
      toast.error("Could not update payment status");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      className={`ml-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-xs font-bold transition-colors ${
        isPaid
          ? "bg-emerald-50 border-emerald-300 text-emerald-800 hover:bg-emerald-100"
          : "bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100"
      } ${busy ? "opacity-50 cursor-wait" : ""}`}
      title={
        isPaid
          ? `Paid${backorder.paid_at ? ` on ${new Date(backorder.paid_at).toLocaleDateString()}` : ""}. Click to mark unpaid.`
          : `Unpaid · Terms: ${backorder.payment_terms || "Net 30"}. Click to mark paid.`
      }
      data-testid="bo-paid-toggle"
      data-state={isPaid ? "paid" : "unpaid"}
    >
      <span
        className={`w-2 h-2 rounded-full ${
          isPaid ? "bg-emerald-500 animate-pulse" : "bg-amber-500"
        }`}
      />
      {isPaid ? "Paid" : `Unpaid · ${backorder.payment_terms || "Net 30"}`}
    </button>
  );
}
